import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, fmtPrice } from "../lib/api";
import { Music2, Sliders, ShoppingBag, LogOut, Plus, Pencil, Trash2, Receipt, X, Users, Tag, Upload, Loader2, BookOpen, KeyRound, RotateCcw, Ban, Eye, EyeOff, Copy, Mail, Inbox, Paperclip, Building2, Save, CheckCircle2, XCircle, ExternalLink, ShieldCheck, Search, WalletCards, Gift } from "lucide-react";
import { toast } from "sonner";
import { formatIdrInput, normalizeIdrInput, parseIdrInput } from "../lib/currencyInput";

const isIdrField = (tab, field, form) => (
  field.key === "price"
  || (tab === "products" && field.key === "upgrade_discount_value" && form.upgrade_discount_type === "amount")
  || (tab === "coupons" && field.key === "discount_value" && form.discount_type === "amount")
);
const engineerProductCategories = ["Sample Pack", "Preset", "Project Template", "E-book", "MIDI Pack", "Audio", "Other"];
const vstPlanGrantOptions = [
  { id: "personal", label: "Personal", detail: "1 plugin · 1 build · 1 perangkat", projectLimit: 1 },
  { id: "creator_personal", label: "Creator", detail: "1 plugin · 3 build · 1 perangkat", projectLimit: 1 },
  { id: "creator", label: "Creator Commercial", detail: "1 plugin komersial · 12 build · 100 serial", projectLimit: 1 },
  { id: "studio", label: "Studio Commercial", detail: "3 plugin komersial · 24 build pool · 1.000 serial/plugin", projectLimit: 3 },
];

const TABS = [
  { id: "songs", label: "Songs", icon: Music2, endpoint: "/songs", admin: "/admin/songs" },
  { id: "gear", label: "Gear", icon: Sliders, endpoint: "/gear", admin: "/admin/gear" },
  { id: "products", label: "Products", icon: ShoppingBag, endpoint: "/admin/products", admin: "/admin/products" },
  { id: "engineer-products", label: "Produk Engineer", icon: ShoppingBag, endpoint: "/admin/engineer-shop/products" },
  { id: "vst-marketplace-products", label: "VST Marketplace", icon: ShoppingBag, endpoint: "/admin/vst-maker/marketplace/products" },
  { id: "vst-marketplace-sales", label: "VST Sales Ledger", icon: Receipt, endpoint: "/admin/vst-maker/marketplace/sales" },
  { id: "blog", label: "Blog", icon: BookOpen, endpoint: "/admin/blog", admin: "/admin/blog" },
  { id: "coupons", label: "Coupons", icon: Tag, endpoint: "/admin/coupons", admin: "/admin/coupons" },
  { id: "licenses", label: "Product Licenses", icon: KeyRound, endpoint: "/admin/licenses" },
  { id: "vst-licenses", label: "VST Maker Licenses", icon: ShieldCheck, endpoint: "/admin/vst-maker/licenses" },
  { id: "customers", label: "Customers", icon: Users, endpoint: "/admin/customers" },
  { id: "inbox", label: "Inbox", icon: Inbox, endpoint: "/admin/inbox" },
  { id: "transactions", label: "Transactions", icon: Receipt, endpoint: "/admin/transactions" },
  { id: "payment-settings", label: "Payment Settings", icon: Building2, endpoint: "/admin/payment-settings" },
  { id: "engineer-payouts", label: "Engineer Payouts", icon: WalletCards, endpoint: "/admin/engineer-shop/payouts" },
  { id: "vst-marketplace-payouts", label: "VST Partner Payouts", icon: WalletCards, endpoint: "/admin/vst-maker/marketplace/payouts" },
  { id: "security", label: "Security", icon: ShieldCheck },
];

const SCHEMAS = {
  songs: [
    { key: "title", label: "Title", required: true },
    { key: "artist", label: "Artist", required: true },
    { key: "genre", label: "Genre", required: true },
    { key: "duration", label: "Duration", placeholder: "3:42", required: true },
    { key: "cover_url", label: "Cover URL", required: true },
    { key: "track_type", label: "Track Type", type: "select", options: ["audio", "youtube", "spotify"], required: true },
    { key: "audio_url", label: "Audio URL (only for 'audio' type)" },
    { key: "embed_url", label: "Embed URL (YouTube/Spotify link)" },
    { key: "release_year", label: "Release Year", type: "number" },
    { key: "description", label: "Description", type: "textarea" },
  ],
  gear: [
    { key: "name", label: "Name", required: true },
    { key: "brand", label: "Brand", required: true },
    { key: "category", label: "Category", required: true },
    { key: "image_url", label: "Image URL", required: true },
    { key: "description", label: "Description", type: "textarea", required: true },
    { key: "specs", label: "Specs (one per line)", type: "lines" },
  ],
  products: [
    { key: "name", label: "Name", required: true },
    { key: "category", label: "Category", required: true },
    { key: "image_url", label: "Image URL", required: true },
    { key: "description", label: "Description", type: "textarea", required: true },
    { key: "asset_type", label: "Tipe aset VST", type: "select", options: ["", "vst_template", "vst_artwork", "vst_ui_kit", "vst_business_plan"], optionLabels: { "": "Produk biasa", vst_template: "Template VST", vst_artwork: "Artwork / PNG", vst_ui_kit: "VST UI Kit", vst_business_plan: "Paket Bisnis VST" } },
    { key: "is_free", label: "Free Product (no payment required)", type: "checkbox" },
    { key: "requires_license", label: "Protect this plugin with a license", type: "checkbox", showWhen: (data) => data.asset_type !== "vst_business_plan", help: "Customers receive one serial automatically after purchase." },
    { key: "max_activations", label: "Computers allowed per license", type: "select", options: ["1", "2", "3"], showWhen: (data) => !!data.requires_license, help: "Customers can deactivate an old computer themselves." },
    { key: "trial_enabled", label: "Offer a free trial", type: "checkbox", showWhen: (data) => !!data.requires_license },
    { key: "trial_days", label: "Trial length (days)", type: "number", showWhen: (data) => !!data.requires_license && !!data.trial_enabled, help: "Seven days is recommended." },
    { key: "price", label: "Price (IDR) — ignored if Free is checked", type: "number", step: "1000", required: true },
    { key: "upgrade_enabled", label: "Diskon untuk pemilik produk lama", type: "checkbox", showWhen: (data) => !data.is_free, help: "Customer yang memiliki salah satu produk asal akan otomatis mendapat harga upgrade." },
    { key: "upgrade_from_product_ids", label: "Produk asal upgrade", type: "product_multi_select", required: true, showWhen: (data) => !data.is_free && !!data.upgrade_enabled, help: "Pilih satu atau beberapa produk yang memberikan hak diskon." },
    { key: "upgrade_discount_type", label: "Jenis diskon upgrade", type: "select", options: ["percent", "amount"], optionLabels: { percent: "Persentase", amount: "Potongan nominal" }, required: true, showWhen: (data) => !data.is_free && !!data.upgrade_enabled },
    { key: "upgrade_discount_value", label: "Nilai diskon upgrade", type: "number", step: "0.01", min: 0.01, maxWhenPercent: 99, required: true, showWhen: (data) => !data.is_free && !!data.upgrade_enabled, help: "Maksimal 99% dan tidak boleh membuat harga menjadi gratis." },
    { key: "preview_audio_url", label: "Preview Audio URL (optional)" },
    {
      key: "preview_youtube_url",
      label: "Preview YouTube URL (optional)",
      type: "url",
      placeholder: "https://www.youtube.com/watch?v=...",
      help: "Video akan tampil tepat di bawah gambar pada halaman detail produk. Mendukung link watch, youtu.be, Shorts, Live, dan embed.",
    },
    {
      key: "download_mode",
      label: "Jenis Download",
      type: "select",
      options: ["platform", "single"],
      optionLabels: {
        platform: "Installer — pilihan Windows / macOS",
        single: "Product / Sample Pack — satu file download",
      },
      required: true,
      showWhen: (data) => data.asset_type !== "vst_business_plan",
    },
    {
      key: "download_url",
      label: "File / Link Download Product",
      required: true,
      type: "file_or_url",
      uploadLabel: "Upload product / sample pack",
      platform: "product",
      storageKey: "product_storage_key",
      filenameKey: "product_download_filename",
      showWhen: (data) => data.asset_type !== "vst_business_plan" && data.download_mode === "single",
    },
    {
      key: "windows_enabled",
      label: "Tersedia untuk Windows",
      type: "checkbox",
      showWhen: (data) => data.asset_type !== "vst_business_plan" && data.download_mode !== "single",
    },
    {
      key: "windows_download_url",
      label: "File / Link Download Windows",
      required: true,
      type: "file_or_url",
      uploadLabel: "Upload installer Windows",
      platform: "windows",
      storageKey: "windows_storage_key",
      filenameKey: "windows_download_filename",
      showWhen: (data) => data.asset_type !== "vst_business_plan" && data.download_mode !== "single" && !!data.windows_enabled,
    },
    {
      key: "macos_enabled",
      label: "Tersedia untuk macOS",
      type: "checkbox",
      showWhen: (data) => data.asset_type !== "vst_business_plan" && data.download_mode !== "single",
    },
    {
      key: "macos_download_url",
      label: "File / Link Download macOS",
      required: true,
      type: "file_or_url",
      uploadLabel: "Upload installer macOS",
      platform: "macos",
      storageKey: "macos_storage_key",
      filenameKey: "macos_download_filename",
      showWhen: (data) => data.asset_type !== "vst_business_plan" && data.download_mode !== "single" && !!data.macos_enabled,
    },
    { key: "status", label: "Publication Status", type: "select", options: ["draft", "published"], required: true, help: "Draft products are hidden from the public shop until you publish them." },
  ],
  blog: [
    { key: "title", label: "Title", required: true },
    { key: "slug", label: "Slug (auto from title if blank)" },
    { key: "excerpt", label: "Excerpt (1-2 lines)", type: "textarea" },
    { key: "featured_image", label: "Featured Image URL", type: "file_or_url" },
    { key: "tags", label: "Tags (one per line)", type: "lines" },
    { key: "author", label: "Author" },
    { key: "status", label: "Status", type: "select", options: ["draft", "published"], required: true },
    { key: "content", label: "Content (Markdown)", type: "textarea_lg", required: true },
  ],
  coupons: [
    { key: "code", label: "Code (e.g. SUMMER20)", required: true },
    {
      key: "coupon_type",
      label: "Coupon Type",
      type: "select",
      options: ["discount", "trial"],
      optionLabels: {
        discount: "Discount",
        trial: "Trial License",
      },
      required: true,
    },
    {
      key: "discount_type",
      label: "Discount Type",
      type: "select",
      options: ["percent", "amount"],
      required: true,
      showWhen: (data) => data.coupon_type !== "trial",
    },
    {
      key: "discount_value",
      label: "Discount Value (% or amount)",
      type: "number",
      step: "0.01",
      required: true,
      showWhen: (data) => data.coupon_type !== "trial",
    },
    {
      key: "discount_scope",
      label: "Discount Applies To",
      type: "select",
      options: ["all", "product"],
      optionLabels: {
        all: "All Products",
        product: "Specific Product",
      },
      required: true,
      showWhen: (data) => data.coupon_type !== "trial",
    },
    {
      key: "discount_product_id",
      label: "Product for Discount",
      type: "product_select",
      excludeFree: true,
      required: true,
      showWhen: (data) =>
        data.coupon_type !== "trial" && data.discount_scope === "product",
    },
    {
      key: "trial_product_id",
      label: "Product for Trial",
      type: "product_select",
      licensedOnly: true,
      required: true,
      showWhen: (data) => data.coupon_type === "trial",
      help: "Hanya product yang menggunakan sistem license yang dapat dipilih.",
    },
    {
      key: "trial_days",
      label: "Trial Duration (1–30 days)",
      type: "number",
      min: 1,
      max: 30,
      required: true,
      showWhen: (data) => data.coupon_type === "trial",
    },
    { key: "expires_at", label: "Berlaku sampai tanggal (opsional)", type: "date", help: "Coupon tetap aktif sampai akhir tanggal yang dipilih." },
    { key: "max_uses", label: "Max uses (0 = unlimited)", type: "number" },
    { key: "active", label: "Active", type: "checkbox" },
  ],
};

export default function AdminDashboard() {
  const { user, loading, isAdmin, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("songs");
  const [items, setItems] = useState([]);
  const [customerProducts, setCustomerProducts] = useState(null);
  const [removingPurchase, setRemovingPurchase] = useState("");
  const [deletingCustomer, setDeletingCustomer] = useState("");

  const openCustomerProducts = async (customer) => {
    try {
      const { data } = await api.get(`/admin/customers/${customer.id}/products`);
      setCustomerProducts({ customer, purchases: data });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memuat produk customer");
    }
  };

  const removeCustomerProduct = async (purchase) => {
    const customer = customerProducts.customer;
    if (!window.confirm(`Hapus pembelian ${purchase.product_name} dari daftar produk ${customer.name} (${customer.email})? Customer dapat membeli atau klaim ulang untuk mendapat serial baru. Riwayat transaksi dan serial lama tetap tersimpan dan serial lama tetap berlaku. Jika ada pembelian lain untuk produk ini, hapus pembelian tersebut juga agar dapat klaim ulang.`)) return;
    setRemovingPurchase(purchase.id);
    try {
      await api.delete(`/admin/customers/${customer.id}/products/${purchase.id}`);
      setCustomerProducts((current) => current && ({ ...current, purchases: current.purchases.filter((p) => p.id !== purchase.id) }));
      toast.success("Produk dihapus dari daftar pembelian customer");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghapus produk customer");
    } finally {
      setRemovingPurchase("");
    }
  };

  const deleteCustomer = async (customer) => {
    const identifier = customer.email || customer.phone || customer.name;
    if (!window.confirm(
      `Hapus akun ${customer.name} (${identifier})?\n\nEmail dan nomor telepon dapat dipakai untuk registrasi ulang. Sesi login lama akan dihentikan. Riwayat transaksi dan lisensi tetap disimpan sebagai arsip dan tidak otomatis berpindah ke akun baru.`,
    )) return;

    setDeletingCustomer(customer.id);
    try {
      await api.delete(`/admin/customers/${encodeURIComponent(customer.id)}`);
      setItems((current) => current.filter((item) => item.id !== customer.id));
      setCustomerProducts((current) => (
        current?.customer?.id === customer.id ? null : current
      ));
      toast.success("Akun customer dihapus. Email dapat digunakan untuk registrasi ulang.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghapus akun customer");
    } finally {
      setDeletingCustomer("");
    }
  };
  const [modal, setModal] = useState(null); // {mode, item}
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingProductField, setUploadingProductField] = useState("");
  const [productUploadProgress, setProductUploadProgress] = useState(0);
  const [licenseGroups, setLicenseGroups] = useState(() => ({}));
  const [manualLicenseEmail, setManualLicenseEmail] = useState("");
  const [manualLicenseProductId, setManualLicenseProductId] = useState("");
  const [creatingLicense, setCreatingLicense] = useState(false);
  const [vstPlanGrantEmail, setVstPlanGrantEmail] = useState("");
  const [vstPlanGrantId, setVstPlanGrantId] = useState("personal");
  const [vstPlanGrantNote, setVstPlanGrantNote] = useState("");
  const [grantingVstPlan, setGrantingVstPlan] = useState(false);
  const [broadcastModal, setBroadcastModal] = useState(false);
  const [broadcastSubject, setBroadcastSubject] = useState("");
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [broadcastTarget, setBroadcastTarget] = useState("all");
  const [broadcastCampaignType, setBroadcastCampaignType] = useState("operational");
  const [broadcastScheduleMode, setBroadcastScheduleMode] = useState("now");
  const [broadcastScheduledAt, setBroadcastScheduledAt] = useState("");
  const [broadcastExcludedProductIds, setBroadcastExcludedProductIds] = useState([]);
  const [broadcastPreview, setBroadcastPreview] = useState(null);
  const [previewingBroadcast, setPreviewingBroadcast] = useState(false);
  const [sendingBroadcastTest, setSendingBroadcastTest] = useState(false);
  const [emailCampaigns, setEmailCampaigns] = useState([]);
  const [loadingEmailCampaigns, setLoadingEmailCampaigns] = useState(false);
  const [emailCampaignSettings, setEmailCampaignSettings] = useState(null);
  const [campaignDetails, setCampaignDetails] = useState(null);
  const [loadingCampaignDetails, setLoadingCampaignDetails] = useState(false);
  const [campaignDeliverySearch, setCampaignDeliverySearch] = useState("");
  const [campaignDeliveryStatus, setCampaignDeliveryStatus] = useState("all");
  const [retryingCampaignDelivery, setRetryingCampaignDelivery] = useState("");
  const [customerEmailModal, setCustomerEmailModal] = useState(null);
  const [customerEmailSubject, setCustomerEmailSubject] = useState("");
  const [customerEmailMessage, setCustomerEmailMessage] = useState("");
  const [sendingCustomerEmail, setSendingCustomerEmail] = useState(false);
  const [products, setProducts] = useState([]);
  const [paymentSettings, setPaymentSettings] = useState({
    doku_enabled: true,
    doku_configured: false,
    doku_mode: "sandbox",
    manual_enabled: false,
    midtrans_enabled: false,
    marketplace_settlement_mode: "manual_payout",
    marketplace_split_enabled: false,
    platform_settlement_account_id: "",
    platform_commission_percent: 15,
    bank_name: "",
    account_number: "",
    account_holder: "",
    instructions: "",
    expiry_hours: 24,
  });
  const [savedMarketplaceMode, setSavedMarketplaceMode] = useState("manual_payout");
  const [savingPaymentSettings, setSavingPaymentSettings] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [showArchivedTransactions, setShowArchivedTransactions] = useState(false);
  const [transactionActionId, setTransactionActionId] = useState("");
  const [payoutActionId, setPayoutActionId] = useState("");
  const [engineerProductActionId, setEngineerProductActionId] = useState("");
  const [engineerProductEdit, setEngineerProductEdit] = useState(null);
  const [engineerProductSearch, setEngineerProductSearch] = useState("");
  const [engineerProductStatus, setEngineerProductStatus] = useState("all");
  const [inboxUnreadCount, setInboxUnreadCount] = useState(0);
  const [inboxStatus, setInboxStatus] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [licenseSearch, setLicenseSearch] = useState("");
  const [vstLicenseDistributions, setVstLicenseDistributions] = useState([]);
  const unreadCountRef = useRef(null);

  const normalizedCustomerSearch = customerSearch.trim().toLowerCase();
  const normalizedLicenseSearch = licenseSearch.trim().toLowerCase();
  const filteredCustomers = tab === "customers"
    ? items.filter((customer) => (
        !normalizedCustomerSearch
        || [customer.name, customer.email].some((value) =>
          String(value || "").toLowerCase().includes(normalizedCustomerSearch)
        )
      ))
    : [];
  const licenseTabActive = tab === "licenses" || tab === "vst-licenses";
  const filteredLicenses = licenseTabActive
    ? items.filter((license) => (
        !normalizedLicenseSearch
        || [license.customer_name, license.customer_email, license.license_key, license.product_name, license.source].some((value) =>
          String(value || "").toLowerCase().includes(normalizedLicenseSearch)
        )
      ))
    : [];
  const filteredVstDistributions = tab === "vst-licenses"
    ? vstLicenseDistributions.filter((plan) => (
        !normalizedLicenseSearch
        || [
          plan.product_name,
          plan.owner_customer_name,
          plan.owner_customer_email,
          plan.status,
          ...(plan.licenses || []).flatMap((license) => [
            license.customer_name, license.customer_email, license.license_key,
          ]),
        ].some((value) => String(value || "").toLowerCase().includes(normalizedLicenseSearch))
      ))
    : [];
  const groupedLicenseItems = tab === "vst-licenses"
    ? filteredLicenses.filter((license) => license.source !== "vst_maker_distribution" && license.license_type !== "vst_maker_distribution")
    : filteredLicenses;
  const filteredEngineerProducts = tab === "engineer-products"
    ? items.filter((product) => {
        const search = engineerProductSearch.trim().toLowerCase();
        const matchesStatus = engineerProductStatus === "all" || product.status === engineerProductStatus;
        const matchesSearch = !search || [product.name, product.owner_engineer_name, product.category]
          .some((value) => String(value || "").toLowerCase().includes(search));
        return matchesStatus && matchesSearch;
      })
    : [];

  // ===============================
  // GROUP LICENSES BY PRODUCT
  // ===============================
  const groupedLicenses =
    licenseTabActive
      ? groupedLicenseItems.reduce((acc, lic) => {
        const key = lic.product_name || "Unknown Product";

        if (!acc[key]) acc[key] = [];

        acc[key].push(lic);

        return acc;
        }, {})
      : {};

  const tabConfig = TABS.find((t) => t.id === tab);

  const load = async () => {
    try {
      if (tab === "security") {
        setItems([]);
      } else if (tab === "inbox") {
        try {
          await api.post("/admin/inbox/sync");
        } catch {
          // The webhook inbox remains available if the optional IMAP sync fails.
        }
        const [messages, status] = await Promise.all([
          api.get(tabConfig.endpoint),
          api.get("/admin/inbox/status"),
        ]);
        setItems(messages.data);
        setInboxStatus(status.data);
      } else if (tab === "vst-licenses") {
        const [licenses, distributions] = await Promise.all([
          api.get(tabConfig.endpoint),
          api.get("/admin/vst-maker/license-distributions"),
        ]);
        setItems(licenses.data);
        setVstLicenseDistributions(distributions.data);
      } else if (tab === "transactions") {
        const transactions = await api.get(tabConfig.endpoint, {
          params: { archived: showArchivedTransactions },
        });
        setItems(transactions.data);
      } else if (tab === "payment-settings") {
        const settings = await api.get(tabConfig.endpoint);
        setItems([]);
        setPaymentSettings(settings.data);
        setSavedMarketplaceMode(settings.data.marketplace_settlement_mode || "manual_payout");
      } else {
        const r = await api.get(tabConfig.endpoint);
        setItems(r.data);
        if (tab === "products") setProducts(r.data);
      }
    } catch {
      toast.error("Failed to load");
    }
  };

  const loadBroadcastProducts = async () => {
  try {

    const r = await api.get("/admin/products");

    setProducts(r.data);

  } catch(err){

    console.log("Failed load products", err);

  }
  };

  const loadEmailCampaigns = async () => {
    setLoadingEmailCampaigns(true);
    try {
      const [campaignResponse, settingsResponse] = await Promise.all([
        api.get("/admin/email-campaigns"),
        api.get("/admin/email-campaign-settings"),
      ]);
      setEmailCampaigns(campaignResponse.data);
      setEmailCampaignSettings(settingsResponse.data);
    } catch (error) {
      console.log("Failed load email campaigns", error);
    } finally {
      setLoadingEmailCampaigns(false);
    }
  };

  useEffect(() => {
  if (isAdmin) {
    load();
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [tab, isAdmin, showArchivedTransactions]);

useEffect(() => {
  if (broadcastModal || tab === "coupons" || tab === "licenses") {
    loadBroadcastProducts();
  }
  if (broadcastModal) {
    loadEmailCampaigns();
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [broadcastModal, tab]);

useEffect(() => {
  if (!broadcastModal) return undefined;
  const interval = window.setInterval(loadEmailCampaigns, 5000);
  return () => window.clearInterval(interval);

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [broadcastModal]);

useEffect(() => {
  setBroadcastPreview(null);
}, [broadcastTarget, broadcastCampaignType, broadcastExcludedProductIds]);

useEffect(() => {
  if (!isAdmin) return undefined;

  const refreshUnread = async () => {
    try {
      try {
        await api.post("/admin/inbox/sync");
      } catch {
        // Continue with locally stored webhook messages.
      }
      const response = await api.get("/admin/inbox/unread-count");
      const nextCount = Number(response.data.unread || 0);
      const previousCount = unreadCountRef.current;
      if (previousCount !== null && nextCount > previousCount) {
        const added = nextCount - previousCount;
        toast.info(`${added} email baru masuk`);
        if (tab === "inbox") {
          const messages = await api.get("/admin/inbox");
          setItems(messages.data);
        }
      }
      unreadCountRef.current = nextCount;
      setInboxUnreadCount(nextCount);
    } catch {
      // Keep the dashboard usable when inbox receiving has not been configured yet.
    }
  };

  refreshUnread();
  const interval = window.setInterval(refreshUnread, 30000);
  return () => window.clearInterval(interval);
}, [isAdmin, tab]);

  if (loading) return <div className="pt-40 text-center text-zinc-500">Loading...</div>;
  if (!isAdmin) return <Navigate to="/admin/login" replace />;

  const openCreate = () => {
    setForm(
      tab === "products"
        ? {
            asset_type: "",
            vst_access_tier: "creator",
            access_duration_days: 30,
            requires_license: true,
            max_activations: "1",
            trial_enabled: true,
            trial_days: 7,
            upgrade_enabled: false,
            upgrade_from_product_ids: [],
            upgrade_discount_type: "percent",
            upgrade_discount_value: 0,
            download_mode: "platform",
            download_url: "",
            product_storage_key: "",
            product_download_filename: "",
            windows_enabled: true,
            windows_download_url: "",
            windows_storage_key: "",
            windows_download_filename: "",
            macos_enabled: false,
            macos_download_url: "",
            macos_storage_key: "",
            macos_download_filename: "",
            status: "draft",
          }
        : tab === "coupons"
          ? {
              coupon_type: "discount",
              discount_type: "percent",
              discount_value: "",
              discount_scope: "all",
              discount_product_id: "",
              trial_product_id: "",
              trial_days: 7,
              expires_at: "",
              max_uses: 0,
              active: true,
            }
          : {},
    );
    setModal({ mode: "create" });
  };
  const openEdit = (item) => {
    const data = { ...item };
    if (data.specs && Array.isArray(data.specs)) data.specs = data.specs.join("\n");
    if (tab === "products") {
      data.asset_type = data.asset_type || "";
      data.vst_access_tier = data.vst_access_tier || "creator";
      data.access_duration_days = Number(data.access_duration_days || 0);
      data.download_mode = data.download_mode === "single" ? "single" : "platform";
      data.windows_enabled = data.windows_enabled !== false;
      data.windows_download_url = data.windows_download_url
        || (data.download_mode === "platform" ? data.download_url : "")
        || "";
      data.macos_enabled = !!data.macos_enabled;
      data.macos_download_url = data.macos_download_url || "";
      data.upgrade_enabled = !!data.upgrade_enabled;
      data.upgrade_from_product_ids = data.upgrade_from_product_ids || [];
      data.upgrade_discount_type = data.upgrade_discount_type || "percent";
      data.upgrade_discount_value = data.upgrade_discount_value || 0;
    } else if (tab === "coupons") {
      data.coupon_type = data.coupon_type || "discount";
      data.discount_product_id = data.discount_product_id || "";
      data.discount_scope = data.discount_scope
        || (data.discount_product_id ? "product" : "all");
      data.trial_product_id = data.trial_product_id || "";
      data.trial_days = data.trial_days || 7;
    }
    setForm(data);
    setModal({ mode: "edit", item });
  };

  const closeModal = () => {
    setModal(null);
    setForm({});
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (tab === "products") {
      if (form.asset_type === "vst_business_plan") {
        // Entitlement products are fulfilled server-side and have no download.
      } else if (form.download_mode === "single") {
        if (!form.download_url?.trim() && !form.product_storage_key?.trim()) {
          toast.error("File atau link product wajib diisi");
          return;
        }
      } else {
        if (!form.windows_enabled && !form.macos_enabled) {
          toast.error("Aktifkan minimal satu platform: Windows atau macOS");
          return;
        }
        if (
          form.windows_enabled
          && !form.windows_download_url?.trim()
          && !form.windows_storage_key?.trim()
        ) {
          toast.error("File atau link Windows wajib diisi");
          return;
        }
        if (
          form.macos_enabled
          && !form.macos_download_url?.trim()
          && !form.macos_storage_key?.trim()
        ) {
          toast.error("File atau link macOS wajib diisi");
          return;
        }
      }
    }
    setSaving(true);
    try {
      const schema = SCHEMAS[tab];
      const payload = {};
      schema.forEach((f) => {
        let v = form[f.key];
        if (f.type === "lines") v = (v || "").split("\n").map((s) => s.trim()).filter(Boolean);
        else if (f.type === "number") v = v === "" || v == null ? null : (isIdrField(tab, f, form) ? parseIdrInput(v) : Number(v));
        else if (f.type === "checkbox") v = !!v;
        else if (f.type === "textarea_lg") v = v ?? "";
        else v = v ?? "";
        payload[f.key] = v;
      });
      if (tab === "products") {
        if (form.asset_type === "vst_business_plan") {
          payload.requires_license = false;
          payload.trial_enabled = false;
        }
        [
          "product_storage_key",
          "product_download_filename",
          "windows_storage_key",
          "windows_download_filename",
          "macos_storage_key",
          "macos_download_filename",
        ].forEach((key) => {
          payload[key] = form[key] || "";
        });
      } else if (tab === "coupons") {
        payload.coupon_type = form.coupon_type || "discount";
        if (payload.coupon_type === "trial") {
          payload.discount_type = "percent";
          payload.discount_value = 0;
          payload.discount_scope = "all";
          payload.discount_product_id = "";
          payload.trial_days = Number(form.trial_days || 7);
          payload.trial_product_id = form.trial_product_id || "";
        } else {
          payload.discount_scope = form.discount_scope || "all";
          payload.discount_product_id = payload.discount_scope === "product"
            ? (form.discount_product_id || "")
            : "";
          payload.trial_days = 7;
          payload.trial_product_id = "";
        }
      }
      if (modal.mode === "create") {
        await api.post(tabConfig.admin, payload);
        toast.success("Created");
      } else {
        await api.put(`${tabConfig.admin}/${modal.item.id}`, payload);
        toast.success("Updated");
      }
      closeModal();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadProductFile = async (file, field) => {
    if (!file) return;
    const isPrivateProductFile = !!field.storageKey;
    const maximumBytes = isPrivateProductFile
      ? 5 * 1024 * 1024 * 1024
      : 500 * 1024 * 1024;
    if (file.size > maximumBytes) {
      toast.error(isPrivateProductFile ? "Ukuran file maksimal 5 GB" : "Ukuran file maksimal 500 MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    if (isPrivateProductFile) {
      fd.append("platform", field.platform);
    } else {
      fd.append("folder", "tripleside/products");
    }
    setUploadingProductField(field.key);
    setProductUploadProgress(0);
    try {
      const r = await api.post(
        isPrivateProductFile ? "/admin/upload/product" : "/admin/upload",
        fd,
        {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
          onUploadProgress: (event) => {
            if (event.total) {
              setProductUploadProgress(Math.round((event.loaded * 100) / event.total));
            }
          },
        },
      );
      if (isPrivateProductFile) {
        setForm((current) => ({
          ...current,
          [field.key]: "",
          [field.storageKey]: r.data.storage_key,
          [field.filenameKey]: r.data.filename,
        }));
        toast.success("File tersimpan privat di Cloudflare R2");
      } else {
        setForm((current) => ({ ...current, [field.key]: r.data.url }));
        toast.success("File uploaded");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload gagal");
    } finally {
      setUploadingProductField("");
      setProductUploadProgress(0);
    }
  };

  const remove = async (item) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`${tabConfig.admin}/${item.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const savePaymentSettings = async (event) => {
    event.preventDefault();
    setSavingPaymentSettings(true);
    try {
      const response = await api.put("/admin/payment-settings", {
        ...paymentSettings,
        expiry_hours: Number(paymentSettings.expiry_hours || 24),
        platform_commission_percent: Number(paymentSettings.platform_commission_percent || 15),
      });
      setPaymentSettings(response.data);
      setSavedMarketplaceMode(response.data.marketplace_settlement_mode || "manual_payout");
      toast.success("Pengaturan pembayaran disimpan");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Pengaturan pembayaran gagal disimpan");
    } finally {
      setSavingPaymentSettings(false);
    }
  };

  const changeAdminPassword = async (event) => {
    event.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("Konfirmasi password baru tidak sama");
      return;
    }
    if (passwordForm.newPassword.length < 12) {
      toast.error("Password baru minimal 12 karakter");
      return;
    }
    if (!window.confirm("Ubah password admin dan keluarkan semua sesi admin yang sedang aktif?")) {
      return;
    }

    setChangingPassword(true);
    try {
      const response = await api.post("/auth/change-password", {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
      });
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      toast.success(response.data.message || "Password admin berhasil diubah");
      logout();
      nav("/admin/login", { replace: true });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Password admin gagal diubah");
    } finally {
      setChangingPassword(false);
    }
  };

  const reviewManualPayment = async (transaction, action) => {
    const approving = action === "approve";
    const promptMessage = approving
      ? `Setujui pembayaran ${transaction.order_id}?`
      : `Alasan penolakan bukti ${transaction.order_id}:`;
    let note = "";
    if (approving) {
      if (!window.confirm(promptMessage)) return;
    } else {
      note = window.prompt(promptMessage, "Bukti pembayaran belum dapat diverifikasi.");
      if (note === null) return;
    }

    try {
      await api.post(`/admin/transactions/${transaction.id}/manual/${action}`, { note });
      toast.success(approving ? "Pembayaran disetujui" : "Bukti pembayaran ditolak");
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Status pembayaran gagal diperbarui");
    }
  };

  const setTransactionArchived = async (transaction, archived) => {
    const reference = transaction.order_id || transaction.session_id || transaction.id;
    const actionLabel = archived ? "arsipkan" : "pulihkan";
    const message = archived
      ? `Arsipkan transaksi ${reference}? Transaksi hanya hilang dari daftar utama admin dan tetap tersedia untuk customer.`
      : `Pulihkan transaksi ${reference} ke daftar utama admin?`;
    if (!window.confirm(message)) return;

    setTransactionActionId(transaction.id);
    try {
      await api.post(
        `/admin/transactions/${transaction.id}/${archived ? "archive" : "restore"}`,
      );
      toast.success(
        archived ? "Transaksi berhasil diarsipkan" : "Transaksi berhasil dipulihkan",
      );
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || `Gagal ${actionLabel} transaksi`);
    } finally {
      setTransactionActionId("");
    }
  };


  const setProductPublication = async (product, publish) => {
    const action = publish ? "publish" : "unpublish";
    const message = publish
      ? `Publish ${product.name}? It will become visible in the public shop.`
      : `Move ${product.name} back to Draft? It will disappear from the public shop.`;
    if (!window.confirm(message)) return;
    try {
      await api.post(`/admin/products/${product.id}/${action}`);
      toast.success(publish ? "Product published" : "Product moved to draft");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Status update failed");
    }
  };

  const copyProductId = async (product) => {
    try {
      await navigator.clipboard.writeText(product.id);
      toast.success("Product ID copied");
    } catch {
      toast.error("Could not copy Product ID");
    }
  };

  const createManualLicense = async (event) => {
    event.preventDefault();
    const email = manualLicenseEmail.trim().toLowerCase();
    if (!email || !manualLicenseProductId) {
      toast.error("Email dan product wajib dipilih");
      return;
    }
    setCreatingLicense(true);
    try {
      const { data } = await api.post("/admin/licenses", {
        email,
        product_id: manualLicenseProductId,
      });
      setManualLicenseEmail("");
      setManualLicenseProductId("");
      toast.success(
        data.email_sent
          ? `Serial ${data.license_key} berhasil dibuat dan dikirim`
          : `Serial ${data.license_key} berhasil dibuat, tetapi email gagal dikirim`,
      );
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Gagal membuat serial");
    } finally {
      setCreatingLicense(false);
    }
  };

  const grantVstBusinessPlan = async (event) => {
    event.preventDefault();
    const email = vstPlanGrantEmail.trim().toLowerCase();
    const selected = vstPlanGrantOptions.find((plan) => plan.id === vstPlanGrantId);
    if (!email || !selected) {
      toast.error("Email dan paket VST wajib dipilih");
      return;
    }
    if (!window.confirm(
      `Berikan paket ${selected.label} gratis kepada ${email}?\n\nPaket akan langsung tersedia untuk ${selected.projectLimit} plugin/project sesuai kuota build paket.`,
    )) return;
    setGrantingVstPlan(true);
    try {
      const { data } = await api.post("/admin/vst-maker/business/grants", {
        email,
        plan_id: selected.id,
        request_id: window.crypto.randomUUID(),
        note: vstPlanGrantNote.trim(),
      });
      setVstPlanGrantEmail("");
      setVstPlanGrantNote("");
      toast.success(`Paket ${data.entitlement.plan_name} diberikan kepada ${data.customer.email}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Gagal memberikan paket VST");
    } finally {
      setGrantingVstPlan(false);
    }
  };

  const resetLicense = async (lic) => {
    if (!window.confirm(`Reset hardware binding for ${lic.license_key}? Customer can then activate on a new computer.`)) return;
    try {
      await api.post(`/admin/licenses/${lic.id}/reset`);
      toast.success("License reset");
      load();
    } catch {
      toast.error("Reset failed");
    }
  };

  const revokeLicense = async (lic) => {
    if (!window.confirm(`Revoke ${lic.license_key}? Customer will no longer be able to use this license.`)) return;
    try {
      await api.post(`/admin/licenses/${lic.id}/revoke`);
      toast.success("License revoked");
      load();
    } catch {
      toast.error("Revoke failed");
    }
  };

  const deleteLicense = async (lic) => {
    if (!window.confirm(`Permanently delete license ${lic.license_key}?`)) return;
    try {
      await api.delete(`/admin/licenses/${lic.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const broadcastAudiencePayload = () => ({
    target: broadcastTarget,
    campaign_type: broadcastCampaignType,
    exclude_product_ids: broadcastExcludedProductIds,
  });

  const previewBroadcastRecipients = async () => {
    setPreviewingBroadcast(true);
    try {
      const response = await api.post(
        "/admin/email-campaign/preview",
        broadcastAudiencePayload(),
      );
      setBroadcastPreview(response.data);
      if (response.data.recipients === 0) {
        toast.warning("No eligible recipients for this campaign");
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not preview recipients");
    } finally {
      setPreviewingBroadcast(false);
    }
  };

  const sendBroadcastTest = async () => {
    if (!broadcastSubject.trim() || !broadcastMessage.trim()) {
      toast.error("Subject and message are required for a test email");
      return;
    }
    setSendingBroadcastTest(true);
    try {
      const response = await api.post("/admin/email-campaign/test", {
        subject: broadcastSubject,
        message: broadcastMessage,
        campaign_type: broadcastCampaignType,
      });
      toast.success(`Test email sent to ${response.data.sent_to}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Test email could not be sent");
    } finally {
      setSendingBroadcastTest(false);
    }
  };

  const sendBroadcastEmail = async () => {

  if (!broadcastSubject.trim()) {
    toast.error("Subject is required");
    return;
  }

  if (!broadcastMessage.trim()) {
    toast.error("Message is required");
    return;
  }

  if (!broadcastPreview) {
    toast.error("Preview recipients before sending");
    return;
  }

  if (broadcastPreview.recipients === 0) {
    toast.error("There are no eligible recipients");
    return;
  }

  let scheduledAt = null;
  if (broadcastScheduleMode === "scheduled") {
    const selectedTime = new Date(broadcastScheduledAt);
    if (!broadcastScheduledAt || Number.isNaN(selectedTime.getTime())) {
      toast.error("Choose a valid campaign schedule");
      return;
    }
    if (selectedTime.getTime() <= Date.now() + 60_000) {
      toast.error("Scheduled time must be at least one minute from now");
      return;
    }
    scheduledAt = selectedTime.toISOString();
  }

  if (!window.confirm(
  `${scheduledAt ? "Schedule" : "Queue"} this ${broadcastCampaignType} email for ${broadcastPreview.recipients} recipients?\n\n${scheduledAt ? `Send time: ${new Date(scheduledAt).toLocaleString()}\n` : ""}The campaign will continue in the background and failed deliveries will be retried automatically.\n\nTargeted: ${broadcastPreview.targeted}\nMarketing consent excluded: ${broadcastPreview.consent_excluded}\nSuppression list excluded: ${broadcastPreview.suppression_excluded || 0}\nProduct excluded: ${broadcastPreview.product_excluded}`
  ))
  return;

  try {

    setSendingBroadcast(true);

  const res = await api.post("/admin/broadcast-email", {
  ...broadcastAudiencePayload(),
  subject: broadcastSubject,
  message: broadcastMessage,
  expected_recipients: broadcastPreview.recipients,
  scheduled_at: scheduledAt,
    });

    toast.success(
      scheduledAt
        ? `Campaign scheduled for ${new Date(res.data.scheduled_at || scheduledAt).toLocaleString()}`
        : `Campaign queued for ${res.data.total ?? broadcastPreview.recipients} customers${res.data.excluded ? `; ${res.data.excluded} excluded` : ""}`
    );

    setBroadcastModal(false);
    setBroadcastSubject("");
    setBroadcastMessage("");
    setBroadcastTarget("all");
    setBroadcastCampaignType("operational");
    setBroadcastScheduleMode("now");
    setBroadcastScheduledAt("");
    setBroadcastExcludedProductIds([]);
    setBroadcastPreview(null);
    loadEmailCampaigns();

  } catch (err) {

    toast.error(
      err?.response?.data?.detail ||
      "Broadcast failed"
    );

  } finally {

    setSendingBroadcast(false);

  }

};

  const openCustomerEmail = (customer) => {
    if (!customer.email) {
      toast.error("Customer does not have an email address");
      return;
    }
    setCustomerEmailSubject("");
    setCustomerEmailMessage("");
    setCustomerEmailModal(customer);
  };

  const closeCustomerEmail = () => {
    if (sendingCustomerEmail) return;
    setCustomerEmailModal(null);
    setCustomerEmailSubject("");
    setCustomerEmailMessage("");
  };

  const sendCustomerEmail = async () => {
    const subject = customerEmailSubject.trim();
    const message = customerEmailMessage.trim();
    if (!subject) {
      toast.error("Subject is required");
      return;
    }
    if (!message) {
      toast.error("Message is required");
      return;
    }
    if (!window.confirm(`Send this email to ${customerEmailModal.name} (${customerEmailModal.email})?`)) return;

    setSendingCustomerEmail(true);
    try {
      await api.post(`/admin/customers/${encodeURIComponent(customerEmailModal.id)}/email`, {
        subject,
        message,
      });
      toast.success(`Email sent to ${customerEmailModal.email}`);
      setCustomerEmailModal(null);
      setCustomerEmailSubject("");
      setCustomerEmailMessage("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Email could not be sent");
    } finally {
      setSendingCustomerEmail(false);
  }
};

  const cancelScheduledCampaign = async (campaign) => {
    if (!window.confirm(`Cancel scheduled campaign “${campaign.subject}”?`)) return;
    try {
      await api.post(`/admin/email-campaigns/${encodeURIComponent(campaign.id)}/cancel`);
      toast.success("Scheduled campaign canceled");
      loadEmailCampaigns();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Campaign could not be canceled");
    }
  };

  const loadCampaignDetails = async (campaignId, search = campaignDeliverySearch, status = campaignDeliveryStatus) => {
    setLoadingCampaignDetails(true);
    try {
      const response = await api.get(`/admin/email-campaigns/${encodeURIComponent(campaignId)}/deliveries`, {
        params: { search: search.trim(), status, limit: 500 },
      });
      setCampaignDetails(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Campaign details could not be loaded");
    } finally {
      setLoadingCampaignDetails(false);
    }
  };

  const openCampaignDetails = (campaign) => {
    setCampaignDeliverySearch("");
    setCampaignDeliveryStatus("all");
    setCampaignDetails({ campaign, deliveries: [], total: campaign.recipients || 0 });
    loadCampaignDetails(campaign.id, "", "all");
  };

  const retryFailedCampaignDeliveries = async (deliveryId = "") => {
    if (!campaignDetails?.campaign?.id) return;
    const label = deliveryId ? "this failed delivery" : "all failed deliveries";
    if (!window.confirm(`Retry ${label}?`)) return;
    setRetryingCampaignDelivery(deliveryId || "all");
    try {
      const response = await api.post(
        `/admin/email-campaigns/${encodeURIComponent(campaignDetails.campaign.id)}/retry-failed`,
        { delivery_ids: deliveryId ? [deliveryId] : [] },
      );
      toast.success(`${response.data.queued} failed deliver${response.data.queued === 1 ? "y" : "ies"} queued again`);
      await Promise.all([
        loadCampaignDetails(campaignDetails.campaign.id),
        loadEmailCampaigns(),
      ]);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed deliveries could not be retried");
    } finally {
      setRetryingCampaignDelivery("");
    }
  };

  const openInboxEmail = async (email) => {
    setSelectedEmail(email);
    setLoadingEmail(true);
    try {
      const response = await api.get(`/admin/inbox/${encodeURIComponent(email.id)}`);
      setSelectedEmail(response.data);
      if (email.unread) {
        setItems((current) => current.map((item) => (
          item.id === email.id ? { ...item, unread: false } : item
        )));
        setInboxUnreadCount((current) => Math.max(0, current - 1));
        unreadCountRef.current = Math.max(0, (unreadCountRef.current || 0) - 1);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membuka email");
      setSelectedEmail(null);
    } finally {
      setLoadingEmail(false);
    }
  };

  const toggleLicenseGroup = (product) => {
  setLicenseGroups((prev) => ({
    ...prev,
    [product]: !(prev[product] ?? true),
  }));
  };

  const handleLogout = () => {
    logout();
    nav("/");
  };

  const processEngineerPayout = async (payout, decision) => {
    if (decision === "paid") {
      const reference = window.prompt("Masukkan nomor referensi transfer:");
      if (!reference?.trim()) return;
      const note = window.prompt("Catatan payout (opsional):") || "";
      setPayoutActionId(payout.id);
      try {
        await api.post(`/admin/engineer-shop/payouts/${payout.id}/paid`, {
          transfer_reference: reference.trim(), note: note.trim(),
        });
        toast.success("Payout ditandai sudah ditransfer");
        await load();
      } catch (error) { toast.error(error?.response?.data?.detail || "Payout gagal diproses"); }
      finally { setPayoutActionId(""); }
      return;
    }
    const note = window.prompt("Tuliskan alasan penolakan payout:");
    if (!note?.trim()) return;
    setPayoutActionId(payout.id);
    try {
      await api.post(`/admin/engineer-shop/payouts/${payout.id}/reject`, { note: note.trim() });
      toast.success("Payout ditolak dan saldo dikembalikan");
      await load();
    } catch (error) { toast.error(error?.response?.data?.detail || "Payout gagal ditolak"); }
    finally { setPayoutActionId(""); }
  };

  const moderateVstMarketplaceProduct = async (product, status) => {
    const note = status === "rejected" ? window.prompt("Alasan penolakan listing:") : "";
    if (status === "rejected" && !note?.trim()) return;
    setEngineerProductActionId(product.id);
    try {
      await api.post(`/admin/vst-maker/marketplace/products/${product.id}/status`, { status, note: note?.trim() || "" });
      toast.success(`Listing ${status === "published" ? "diterbitkan" : status === "archived" ? "diarsipkan" : "ditolak"}`);
      await load();
    } catch (error) { toast.error(error?.response?.data?.detail || "Moderasi listing gagal"); }
    finally { setEngineerProductActionId(""); }
  };

  const processVstPartnerPayout = async (payout, decision) => {
    const reference = decision === "paid" ? window.prompt("Masukkan nomor referensi transfer:") : "";
    if (decision === "paid" && !reference?.trim()) return;
    const note = window.prompt(decision === "paid" ? "Catatan payout (opsional):" : "Alasan penolakan payout:") || "";
    if (decision === "reject" && !note.trim()) return;
    setPayoutActionId(payout.id);
    try {
      await api.post(`/admin/vst-maker/marketplace/payouts/${payout.id}/${decision}`, { reference: reference?.trim() || "", note: note.trim() });
      toast.success(decision === "paid" ? "Payout Partner ditandai sudah ditransfer" : "Payout ditolak dan saldo dikembalikan");
      await load();
    } catch (error) { toast.error(error?.response?.data?.detail || "Payout Partner gagal diproses"); }
    finally { setPayoutActionId(""); }
  };

  const reconcileVstSale = async (sale) => {
    const fee = window.prompt("Biaya gateway final (IDR):", String(sale.gateway_fee || 0));
    if (fee === null || Number.isNaN(Number(fee)) || Number(fee) < 0) return;
    const reference = window.prompt("Referensi settlement gateway:");
    if (!reference?.trim()) return;
    setPayoutActionId(sale.id);
    try {
      await api.post(`/admin/vst-maker/marketplace/sales/${sale.transaction_id}/settlement`, { gateway_fee: Number(fee), reference: reference.trim() });
      toast.success("Settlement direkonsiliasi"); await load();
    } catch (error) { toast.error(error?.response?.data?.detail || "Rekonsiliasi gagal"); }
    finally { setPayoutActionId(""); }
  };

  const adjustVstSale = async (sale, status) => {
    const reference = window.prompt(`Referensi ${status}:`);
    if (!reference?.trim()) return;
    const note = window.prompt("Catatan (opsional):") || "";
    setPayoutActionId(sale.id);
    try {
      await api.post(`/admin/vst-maker/marketplace/sales/${sale.transaction_id}/adjust`, { status, reference: reference.trim(), note: note.trim() });
      toast.success(`Penjualan ditandai ${status}`); await load();
    } catch (error) { toast.error(error?.response?.data?.detail || "Penyesuaian penjualan gagal"); }
    finally { setPayoutActionId(""); }
  };

  const saveEngineerProduct = async (event) => {
    event.preventDefault();
    if (!engineerProductEdit) return;
    setEngineerProductActionId(engineerProductEdit.id);
    try {
      await api.put(`/admin/engineer-shop/products/${engineerProductEdit.id}`, {
        name: engineerProductEdit.name,
        category: engineerProductEdit.category,
        description: engineerProductEdit.description,
        price: parseIdrInput(engineerProductEdit.price),
        image_url: engineerProductEdit.image_url,
        preview_audio_url: engineerProductEdit.preview_audio_url || "",
        preview_youtube_url: engineerProductEdit.preview_youtube_url || "",
        license_name: engineerProductEdit.license_name || "Personal & Commercial Use",
        license_terms: engineerProductEdit.license_terms || "",
      });
      toast.success("Produk engineer diperbarui");
      setEngineerProductEdit(null);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Produk engineer gagal diperbarui");
    } finally {
      setEngineerProductActionId("");
    }
  };

  const setEngineerProductState = async (product, status) => {
    const labels = { published: "terbitkan", draft: "jadikan draft", archived: "arsipkan" };
    const detail = status === "archived"
      ? "Produk akan hilang dari katalog, tetapi riwayat transaksi dan akses pembeli lama tetap tersedia."
      : status === "draft" ? "Produk akan disembunyikan dari katalog publik." : "Produk akan tampil di katalog publik.";
    if (!window.confirm(`${labels[status]} ${product.name}? ${detail}`)) return;
    setEngineerProductActionId(product.id);
    try {
      await api.post(`/admin/engineer-shop/products/${product.id}/status`, { status });
      toast.success(status === "published" ? "Produk diterbitkan" : status === "draft" ? "Produk dijadikan draft" : "Produk diarsipkan");
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Status produk gagal diperbarui");
    } finally {
      setEngineerProductActionId("");
    }
  };

  const permanentlyDeleteEngineerProduct = async (product) => {
    if (!product.can_permanently_delete) return;
    if (!window.confirm(`Hapus permanen ${product.name}? Tindakan ini tidak dapat dibatalkan dan file produk juga akan dihapus.`)) return;
    setEngineerProductActionId(product.id);
    try {
      await api.delete(`/admin/engineer-shop/products/${product.id}`);
      toast.success("Produk engineer dihapus permanen");
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Produk engineer gagal dihapus");
    } finally {
      setEngineerProductActionId("");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen pt-20 pb-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#e11d48] mb-2">Studio Admin</div>
            <h1 className="font-[Outfit] text-4xl font-black tracking-tighter">Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/vst-assets"
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-sm font-semibold text-zinc-300 hover:bg-white/5"
            >
              <ShoppingBag className="w-3.5 h-3.5" /> VST Maker
            </Link>
            <Link
              to="/admin/vst-maker"
              data-testid="vst-maker-admin-link"
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#fb7185]/30 text-sm font-semibold text-[#fda4af] hover:bg-[#fb7185]/10"
            >
              <Music2 className="w-3.5 h-3.5" /> VST Studio
            </Link>
            <Link
              to="/admin/engineers"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#e11d48] text-sm font-semibold hover:bg-[#be123c]"
            >
              <Users className="w-3.5 h-3.5" /> Engineers
            </Link>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-sm hover:bg-white/5"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-[220px_1fr] gap-6">
          <aside className="bg-[#0a0a0c] border border-white/10 rounded-2xl p-3 h-fit">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  data-testid={`tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors mb-1 ${
                    tab === t.id ? "bg-[#e11d48] text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <Icon className="w-4 h-4" /> {t.label}
                  {t.id === "inbox" && inboxUnreadCount > 0 && (
                    <span className="ml-auto min-w-5 rounded-full bg-[#e11d48] px-1.5 py-0.5 text-center text-[10px] font-bold text-white">
                      {inboxUnreadCount > 99 ? "99+" : inboxUnreadCount}
                    </span>
                  )}
                </button>
              );
            })}
          </aside>

          <section className="bg-[#0a0a0c] border border-white/10 rounded-2xl p-6">
  <div className="flex items-center justify-between mb-6">
    <div>
      <h2 className="font-[Outfit] text-2xl font-bold">
        {tabConfig.label}
      </h2>
      {tab !== "security" && tab !== "payment-settings" && (
        <p className="text-xs text-zinc-500 mt-0.5">
          {tab === "customers"
            ? `${filteredCustomers.length} dari ${items.length} customer`
            : licenseTabActive
              ? tab === "vst-licenses"
                ? `${groupedLicenseItems.length} lisensi biasa · ${filteredVstDistributions.length} paket distribusi`
                : `${filteredLicenses.length} dari ${items.length} license`
              : tab === "engineer-products"
                ? `${filteredEngineerProducts.length} dari ${items.length} produk engineer`
              : `${items.length} total`}
        </p>
      )}
    </div>

    <div className="flex items-center gap-2">

      {customerProducts && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Produk customer">
          <div className="bg-zinc-950 border border-white/10 rounded-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-auto">
            <div className="flex justify-between gap-4 mb-4">
              <h2 className="font-bold">Produk — {customerProducts.customer.name}</h2>
              <button disabled={!!removingPurchase} onClick={() => setCustomerProducts(null)}>Tutup</button>
            </div>
            <p className="text-sm text-zinc-400 mb-4">{customerProducts.customer.email}. Hapus pembelian dari daftar produk agar customer dapat membeli atau klaim ulang. Serial lama tetap berlaku dan riwayat transaksi tetap tersimpan.</p>
            {customerProducts.purchases.length === 0 && <p>Belum ada produk yang dibeli.</p>}
            {customerProducts.purchases.map((purchase) => (
              <div key={purchase.id} className="flex items-center justify-between gap-4 py-3 border-t border-white/10">
                <div><div>{purchase.product_name || purchase.product_id}</div><div className="text-xs text-zinc-500">{purchase.created_at?.slice(0, 10)} · {purchase.id}</div></div>
                <button disabled={!!removingPurchase} onClick={() => removeCustomerProduct(purchase)} className="text-red-400 disabled:opacity-50">{removingPurchase === purchase.id ? "Menghapus…" : "Hapus produk"}</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "customers" && (
        <button
          onClick={() => setBroadcastModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#e11d48] hover:bg-[#be123c] text-sm font-semibold transition-colors"
        >
          <Mail className="w-4 h-4" />
          Broadcast Email
        </button>
      )}

      {tab === "transactions" && (
        <div className="inline-flex rounded-full border border-white/10 bg-black/30 p-1">
          <button
            type="button"
            onClick={() => setShowArchivedTransactions(false)}
            className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
              !showArchivedTransactions
                ? "bg-[#e11d48] text-white"
                : "text-zinc-400 hover:text-white"
            }`}
          >
            <Receipt className="h-3.5 w-3.5" />
            Aktif
          </button>
          <button
            type="button"
            onClick={() => setShowArchivedTransactions(true)}
            className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
              showArchivedTransactions
                ? "bg-[#e11d48] text-white"
                : "text-zinc-400 hover:text-white"
            }`}
          >
            <EyeOff className="h-3.5 w-3.5" />
            Diarsipkan
          </button>
        </div>
      )}

      {tab !== "transactions" &&
        tab !== "payment-settings" &&
        tab !== "customers" &&
        tab !== "inbox" &&
        tab !== "licenses" &&
        tab !== "vst-licenses" &&
        tab !== "engineer-products" &&
        tab !== "security" && (
          <button
            data-testid="add-item-btn"
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#e11d48] hover:bg-[#be123c] text-sm font-semibold transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add new
          </button>
        )}

    </div>
  </div>

            {(tab === "customers" || licenseTabActive) && (
              <div className="relative mb-6">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                <input
                  type="search"
                  data-testid={`${tab}-search`}
                  value={tab === "customers" ? customerSearch : licenseSearch}
                  onChange={(event) => {
                    if (tab === "customers") setCustomerSearch(event.target.value);
                    else setLicenseSearch(event.target.value);
                  }}
                  placeholder={tab === "customers"
                    ? "Cari customer berdasarkan nama atau email..."
                    : "Cari berdasarkan customer, serial, produk, atau sumber..."}
                  className="w-full rounded-xl border border-white/10 bg-black/30 py-3 pl-10 pr-11 text-sm text-white outline-none placeholder:text-zinc-600 focus:border-[#e11d48] focus:ring-1 focus:ring-[#e11d48]/30"
                  aria-label={tab === "customers" ? "Cari customer" : `Cari license ${tab === "vst-licenses" ? "VST Maker" : "produk toko"}`}
                />
                {(tab === "customers" ? customerSearch : licenseSearch) && (
                  <button
                    type="button"
                    onClick={() => {
                      if (tab === "customers") setCustomerSearch("");
                      else setLicenseSearch("");
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-zinc-500 transition hover:bg-white/5 hover:text-white"
                    aria-label="Hapus pencarian"
                    title="Hapus pencarian"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}


            {tab === "payment-settings" && (
              <form
                onSubmit={savePaymentSettings}
                className="mb-6 rounded-2xl border border-white/10 bg-black/20 p-5"
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5 mb-5">
                  <div>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-[#e11d48]" />
                      <h3 className="font-[Outfit] text-lg font-bold">Payment Settings</h3>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">
                      Atur pembayaran customer dan cara payout engineer. Kredensial API hanya disimpan di environment server.
                    </p>
                  </div>
                  <button
                    type="submit"
                    disabled={savingPaymentSettings}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#e11d48] hover:bg-[#be123c] text-sm font-semibold disabled:opacity-50"
                  >
                    {savingPaymentSettings ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Simpan pengaturan
                  </button>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className={`rounded-xl border px-4 py-3 ${
                    paymentSettings.doku_configured
                      ? "border-emerald-500/20 bg-emerald-500/5"
                      : "border-amber-500/20 bg-amber-500/5"
                  }`}>
                    <div className="text-xs text-zinc-500">Status integrasi DOKU</div>
                    <div className={`mt-1 text-sm font-semibold ${
                      paymentSettings.doku_configured ? "text-emerald-400" : "text-amber-300"
                    }`}>
                      {paymentSettings.doku_configured
                        ? `Terhubung (${paymentSettings.doku_mode || "sandbox"})`
                        : "Menunggu kredensial API"}
                    </div>
                  </div>
                  <label className="text-xs text-zinc-400">
                    Kedaluwarsa checkout DOKU (jam)
                    <input
                      type="number"
                      min="1"
                      max="168"
                      value={paymentSettings.expiry_hours || 24}
                      onChange={(event) => setPaymentSettings((current) => ({ ...current, expiry_hours: event.target.value }))}
                      className="mt-2 w-full bg-[#050505] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#e11d48]"
                    />
                  </label>
                </div>

                <div className="grid md:grid-cols-2 gap-3 mt-4">
                  <label className="flex items-center justify-between gap-4 rounded-xl border border-white/10 px-4 py-3">
                    <div>
                      <div className="text-sm font-semibold">Tampilkan DOKU</div>
                      <div className="text-xs text-zinc-500 mt-0.5">Aktif hanya jika kredensial server lengkap.</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={!!paymentSettings.doku_enabled}
                      onChange={(event) => setPaymentSettings((current) => ({ ...current, doku_enabled: event.target.checked }))}
                      className="w-4 h-4 accent-[#e11d48]"
                    />
                  </label>
                  <label className="flex items-center justify-between gap-4 rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3">
                    <div>
                      <div className="text-sm font-semibold">Tampilkan Midtrans</div>
                      <div className="text-xs text-zinc-500 mt-0.5">Biarkan mati sampai akun Midtrans disetujui.</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={!!paymentSettings.midtrans_enabled}
                      onChange={(event) => setPaymentSettings((current) => ({ ...current, midtrans_enabled: event.target.checked }))}
                      className="w-4 h-4 accent-[#e11d48]"
                    />
                  </label>
                </div>

                {paymentSettings.doku_enabled && !paymentSettings.doku_configured && (
                    <div className="mt-4 text-xs text-amber-300 border border-amber-500/20 bg-amber-500/5 rounded-xl px-4 py-3">
                      Isi DOKU_CLIENT_ID dan DOKU_SECRET_KEY di environment backend agar tombol DOKU muncul di toko.
                    </div>
                )}

                <div className="mt-5 rounded-2xl border border-[#e11d48]/20 bg-[#e11d48]/5 p-5">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-[#fb7185]" />
                        <h4 className="text-sm font-bold">Mode payout marketplace</h4>
                      </div>
                      <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
                        Pilihan berlaku untuk checkout baru. Pesanan lama tetap mengikuti mode yang tersimpan saat pembayarannya dibuat.
                      </p>
                      <p className="text-xs text-emerald-300 mt-2">Aktif sekarang: {savedMarketplaceMode === "doku_split" ? "DOKU Split Settlement" : "Transfer manual ke engineer"}{paymentSettings.marketplace_settlement_mode !== savedMarketplaceMode ? " · Perubahan belum disimpan" : ""}</p>
                      <p className="text-xs text-zinc-500 mt-2 max-w-2xl">
                        Setelah DOKU menyetujui Split Settlement, pilih mode DOKU, isi Settlement Bank ID platform dan setiap engineer terverifikasi, lalu klik Simpan pengaturan. Persetujuan DOKU tidak terdeteksi otomatis oleh website.
                      </p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3 mt-5">
                    <label className={`rounded-xl border p-4 cursor-pointer ${paymentSettings.marketplace_settlement_mode !== "doku_split" ? "border-emerald-400/40 bg-emerald-400/5" : "border-white/10"}`}>
                      <div className="flex items-start gap-3"><input type="radio" name="settlement-mode" value="manual_payout" checked={paymentSettings.marketplace_settlement_mode !== "doku_split"} onChange={() => setPaymentSettings((current) => ({ ...current, marketplace_settlement_mode: "manual_payout", marketplace_split_enabled: false }))} className="mt-1 accent-[#e11d48]" /><div><div className="text-sm font-semibold">Transfer manual ke engineer</div><div className="text-xs text-zinc-500 mt-1">Customer membayar via DOKU biasa. Setelah hasil disetujui, Anda transfer ke rekening engineer dan mencatat referensinya.</div></div></div>
                    </label>
                    <label className={`rounded-xl border p-4 cursor-pointer ${paymentSettings.marketplace_settlement_mode === "doku_split" ? "border-[#e11d48]/50 bg-[#e11d48]/5" : "border-white/10"}`}>
                      <div className="flex items-start gap-3"><input type="radio" name="settlement-mode" value="doku_split" checked={paymentSettings.marketplace_settlement_mode === "doku_split"} onChange={() => setPaymentSettings((current) => ({ ...current, marketplace_settlement_mode: "doku_split", marketplace_split_enabled: true }))} className="mt-1 accent-[#e11d48]" /><div><div className="text-sm font-semibold">DOKU Split Settlement</div><div className="text-xs text-zinc-500 mt-1">Dana ditahan, lalu otomatis dibagi dan dilepas setelah customer menyetujui hasil.</div></div></div>
                    </label>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mt-5">
                    <label className="text-xs text-zinc-400">
                      Settlement Bank ID platform
                      <input
                        value={paymentSettings.platform_settlement_account_id || ""}
                        disabled={paymentSettings.marketplace_settlement_mode !== "doku_split"}
                        onChange={(event) => setPaymentSettings((current) => ({ ...current, platform_settlement_account_id: event.target.value }))}
                        className="mt-2 w-full bg-[#050505] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#e11d48]"
                        placeholder="SBA-0001-123456789"
                      />
                    </label>
                    <label className="text-xs text-zinc-400">
                      Komisi platform (%)
                      <input
                        type="number"
                        min="1"
                        max="99"
                        value={paymentSettings.platform_commission_percent || 15}
                        onChange={(event) => setPaymentSettings((current) => ({ ...current, platform_commission_percent: event.target.value }))}
                        className="mt-2 w-full bg-[#050505] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#e11d48]"
                      />
                    </label>
                  </div>
                  {paymentSettings.marketplace_settlement_mode === "doku_split" && (!paymentSettings.doku_configured || !paymentSettings.platform_settlement_account_id) && (
                    <div className="mt-4 rounded-xl border border-amber-400/20 bg-amber-400/5 px-4 py-3 text-xs text-amber-200">
                      Konfigurasi belum lengkap. Checkout marketplace tetap diblokir untuk mencegah settlement masuk ke rekening yang salah.
                    </div>
                  )}
                  {paymentSettings.marketplace_settlement_mode !== "doku_split" && (
                    <div className="mt-4 rounded-xl border border-emerald-400/20 bg-emerald-400/5 px-4 py-3 text-xs text-emerald-200">
                      Mode sementara aktif: DOKU menerima pembayaran customer tanpa instruksi split/hold. Payout engineer dicatat manual setelah customer menyetujui hasil.
                    </div>
                  )}
                </div>
              </form>
            )}

            {tab === "inbox" && inboxStatus && !inboxStatus.webhook_configured && !inboxStatus.imap_configured && (
              <div className="mb-6 rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-sm text-amber-200">
                Tampilan inbox sudah siap. Hubungkan mailbox IONOS melalui konfigurasi IMAP, atau gunakan webhook Resend
                <span className="font-mono"> email.received</span>, agar email mulai masuk.
              </div>
            )}

            {tab === "inbox" && inboxStatus?.inbound_address && (
              <div className="mb-6 flex items-center gap-2 rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-zinc-300">
                <Inbox className="h-4 w-4 text-[#fb7185]" />
                Alamat masuk: <span className="font-mono text-white">{inboxStatus.inbound_address}</span>
              </div>
            )}

            <div className="overflow-x-auto">
              {tab === "payment-settings" ? null : tab === "security" ? (
                <form
                  onSubmit={changeAdminPassword}
                  className="max-w-2xl rounded-2xl border border-white/10 bg-black/20 p-5 sm:p-6"
                >
                  <div className="flex items-start gap-3 mb-6">
                    <div className="rounded-xl bg-[#e11d48]/10 p-3 text-[#fb7185]">
                      <ShieldCheck className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-[Outfit] text-xl font-bold">Reset Password Admin</h3>
                      <p className="text-xs leading-5 text-zinc-500 mt-1">
                        Masukkan password saat ini untuk membuat password baru. Setelah berhasil,
                        semua sesi admin akan dikeluarkan dan Anda harus login kembali.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {[
                      {
                        key: "currentPassword",
                        label: "Password saat ini",
                        autoComplete: "current-password",
                      },
                      {
                        key: "newPassword",
                        label: "Password baru",
                        autoComplete: "new-password",
                      },
                      {
                        key: "confirmPassword",
                        label: "Ulangi password baru",
                        autoComplete: "new-password",
                      },
                    ].map((field) => (
                      <label key={field.key} className="block text-xs text-zinc-400">
                        {field.label}
                        <div className="relative mt-2">
                          <input
                            type={visiblePasswords[field.key] ? "text" : "password"}
                            autoComplete={field.autoComplete}
                            required
                            minLength={field.key === "currentPassword" ? 1 : 12}
                            maxLength={72}
                            value={passwordForm[field.key]}
                            onChange={(event) =>
                              setPasswordForm((current) => ({
                                ...current,
                                [field.key]: event.target.value,
                              }))
                            }
                            className="w-full rounded-xl border border-white/10 bg-[#050505] px-4 py-3 pr-12 text-sm text-white focus:border-[#e11d48] focus:outline-none"
                          />
                          <button
                            type="button"
                            aria-label={visiblePasswords[field.key] ? `Sembunyikan ${field.label}` : `Tampilkan ${field.label}`}
                            onClick={() =>
                              setVisiblePasswords((current) => ({
                                ...current,
                                [field.key]: !current[field.key],
                              }))
                            }
                            className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-zinc-500 hover:text-white"
                          >
                            {visiblePasswords[field.key] ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </label>
                    ))}
                  </div>

                  <div className="mt-5 rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-xs leading-5 text-amber-200">
                    Gunakan minimal 12 karakter yang memiliki huruf besar, huruf kecil, angka,
                    dan simbol. Password tidak pernah disimpan sebagai teks biasa.
                  </div>

                  <button
                    type="submit"
                    disabled={changingPassword}
                    className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#e11d48] px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-[#be123c] disabled:opacity-50"
                  >
                    {changingPassword ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <KeyRound className="w-4 h-4" />
                    )}
                    {changingPassword ? "Mengubah password..." : "Ubah password admin"}
                  </button>
                </form>
              ) : tab === "inbox" ? (
                <table className="w-full text-sm">
                  <thead className="border-b border-white/10 text-[10px] uppercase tracking-wider text-zinc-500">
                    <tr>
                      <th className="py-3 px-3 text-left">From</th>
                      <th className="py-3 px-3 text-left">Subject</th>
                      <th className="py-3 px-3 text-left">Received</th>
                      <th className="py-3 px-3 text-right">Files</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((email) => (
                      <tr
                        key={email.id}
                        data-testid={`inbox-email-${email.id}`}
                        onClick={() => openInboxEmail(email)}
                        className={`cursor-pointer border-b border-white/5 transition-colors hover:bg-white/[0.03] ${email.unread ? "bg-[#e11d48]/5" : ""}`}
                      >
                        <td className="py-3 px-3">
                          <div className={`max-w-[260px] truncate ${email.unread ? "font-bold text-white" : "text-zinc-300"}`}>{email.from || "Unknown sender"}</div>
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            {email.unread && <span className="h-2 w-2 shrink-0 rounded-full bg-[#e11d48]" aria-label="Unread" />}
                            <span className={`max-w-[360px] truncate ${email.unread ? "font-semibold text-white" : "text-zinc-400"}`}>{email.subject || "(No subject)"}</span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap py-3 px-3 font-mono text-xs text-zinc-500">
                          {email.received_at ? new Date(email.received_at).toLocaleString("id-ID") : "—"}
                        </td>
                        <td className="py-3 px-3 text-right text-xs text-zinc-500">
                          {email.attachments?.length ? (
                            <span className="inline-flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" /> {email.attachments.length}</span>
                          ) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : tab === "vst-marketplace-products" ? (
                <div className="overflow-x-auto"><table className="w-full text-sm"><thead className="border-b border-white/10 text-[10px] uppercase tracking-wider text-zinc-500"><tr><th className="px-3 py-3 text-left">Plugin</th><th className="px-3 py-3 text-left">Partner</th><th className="px-3 py-3 text-left">Harga</th><th className="px-3 py-3 text-left">Model</th><th className="px-3 py-3 text-left">Status</th><th className="px-3 py-3 text-right">Moderasi</th></tr></thead><tbody>{items.map((product) => <tr key={product.id} className="border-b border-white/5"><td className="px-3 py-4"><div className="font-semibold">{product.name}</div><div className="mt-1 text-[10px] text-zinc-500">{product.has_artifact ? "Artifact tersedia" : "Artifact tidak tersedia"} · {product.vst_build_job_id}</div></td><td className="px-3 py-4 font-mono text-[10px] text-zinc-500">{product.owner_vst_customer_id}</td><td className="px-3 py-4 font-semibold">{fmtPrice(product.price)}</td><td className="px-3 py-4">{product.marketplace_mode} · {product.marketplace_commission_percent}%</td><td className="px-3 py-4"><span className="rounded-full bg-white/[.06] px-2 py-1 text-[10px] uppercase">{product.status}</span>{product.moderation_note && <div className="mt-1 max-w-xs text-[10px] text-rose-300">{product.moderation_note}</div>}</td><td className="px-3 py-4 text-right"><div className="inline-flex gap-2">{product.status !== "published" && <button onClick={() => moderateVstMarketplaceProduct(product, "published")} disabled={engineerProductActionId === product.id} className="rounded-full bg-emerald-500 px-3 py-2 text-xs font-bold text-black">Terbitkan</button>}<button onClick={() => moderateVstMarketplaceProduct(product, "rejected")} disabled={engineerProductActionId === product.id} className="rounded-full border border-rose-400/30 px-3 py-2 text-xs text-rose-200">Tolak</button>{product.status !== "archived" && <button onClick={() => moderateVstMarketplaceProduct(product, "archived")} disabled={engineerProductActionId === product.id} className="rounded-full border border-white/15 px-3 py-2 text-xs">Arsipkan</button>}</div></td></tr>)}</tbody></table>{items.length === 0 && <div className="py-16 text-center text-sm text-zinc-500">Belum ada listing VST.</div>}</div>
              ) : tab === "vst-marketplace-sales" ? (
                <div className="overflow-x-auto"><table className="w-full min-w-[1100px] text-sm"><thead className="border-b border-white/10 text-[10px] uppercase tracking-wider text-zinc-500"><tr><th className="px-3 py-3 text-left">Order</th><th className="px-3 py-3 text-left">Produk</th><th className="px-3 py-3 text-left">Bruto</th><th className="px-3 py-3 text-left">Gateway</th><th className="px-3 py-3 text-left">Komisi</th><th className="px-3 py-3 text-left">Partner</th><th className="px-3 py-3 text-left">Status</th><th className="px-3 py-3 text-right">Tindakan</th></tr></thead><tbody>{items.map((sale) => <tr key={sale.id} className="border-b border-white/5"><td className="px-3 py-4"><div>{sale.order_id || "—"}</div><div className="mt-1 font-mono text-[10px] text-zinc-600">{sale.transaction_id}</div></td><td className="px-3 py-4">{sale.product_name}</td><td className="px-3 py-4">{fmtPrice(sale.gross_amount)}</td><td className="px-3 py-4">{fmtPrice(sale.gateway_fee)} · {sale.gateway_fee_final ? "final" : "estimasi"}</td><td className="px-3 py-4">{fmtPrice(sale.platform_fee)} ({sale.platform_commission_percent}%)</td><td className="px-3 py-4 font-semibold text-emerald-300">{fmtPrice(sale.partner_earning)}</td><td className="px-3 py-4">{sale.sale_status || sale.payout_status}</td><td className="px-3 py-4 text-right"><div className="inline-flex gap-2">{!sale.gateway_fee_final && <button onClick={() => reconcileVstSale(sale)} disabled={payoutActionId === sale.id} className="rounded-full bg-sky-400 px-3 py-2 text-xs font-bold text-black">Rekonsiliasi</button>}{!["paid", "refunded", "chargeback"].includes(sale.payout_status) && <><button onClick={() => adjustVstSale(sale, "refunded")} disabled={payoutActionId === sale.id} className="rounded-full border border-amber-400/30 px-3 py-2 text-xs text-amber-200">Refund</button><button onClick={() => adjustVstSale(sale, "chargeback")} disabled={payoutActionId === sale.id} className="rounded-full border border-rose-400/30 px-3 py-2 text-xs text-rose-200">Chargeback</button></>}</div></td></tr>)}</tbody></table>{items.length === 0 && <div className="py-16 text-center text-sm text-zinc-500">Belum ada ledger penjualan VST.</div>}</div>
              ) : tab === "vst-marketplace-payouts" ? (
                <div className="overflow-x-auto"><table className="w-full text-sm"><thead className="border-b border-white/10 text-[10px] uppercase tracking-wider text-zinc-500"><tr><th className="px-3 py-3 text-left">Partner</th><th className="px-3 py-3 text-left">Rekening</th><th className="px-3 py-3 text-left">Nominal</th><th className="px-3 py-3 text-left">Status</th><th className="px-3 py-3 text-right">Tindakan</th></tr></thead><tbody>{items.map((payout) => <tr key={payout.id} className="border-b border-white/5"><td className="px-3 py-4"><div className="font-mono text-xs">{payout.partner_customer_id}</div><div className="mt-1 text-[10px] text-zinc-500">{payout.earning_ids?.length || 0} transaksi</div></td><td className="px-3 py-4 text-xs">{payout.bank?.bank_name} · {payout.bank?.account_number}<div className="mt-1 text-zinc-600">{payout.bank?.account_holder}</div></td><td className="px-3 py-4 font-semibold">{fmtPrice(payout.amount)}</td><td className="px-3 py-4">{payout.status}{payout.reference && <div className="mt-1 text-[10px] text-zinc-500">Ref. {payout.reference}</div>}</td><td className="px-3 py-4 text-right">{payout.status === "pending" && <div className="inline-flex gap-2"><button onClick={() => processVstPartnerPayout(payout, "paid")} disabled={payoutActionId === payout.id} className="rounded-full bg-emerald-500 px-3 py-2 text-xs font-bold text-black">Sudah transfer</button><button onClick={() => processVstPartnerPayout(payout, "reject")} disabled={payoutActionId === payout.id} className="rounded-full border border-rose-400/30 px-3 py-2 text-xs text-rose-200">Tolak</button></div>}</td></tr>)}</tbody></table>{items.length === 0 && <div className="py-16 text-center text-sm text-zinc-500">Belum ada payout VST Partner.</div>}</div>
              ) : tab === "engineer-products" ? (
                <div className="space-y-5">
                  <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                    <div className="relative flex-1 max-w-md"><Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" /><input value={engineerProductSearch} onChange={(event) => setEngineerProductSearch(event.target.value)} className="w-full rounded-xl border border-white/10 bg-black py-2.5 pl-10 pr-4 text-sm" placeholder="Cari produk atau engineer..." /></div>
                    <select value={engineerProductStatus} onChange={(event) => setEngineerProductStatus(event.target.value)} className="rounded-xl border border-white/10 bg-black px-4 py-2.5 text-sm"><option value="all">Semua status</option><option value="published">Published</option><option value="draft">Draft</option><option value="archived">Archived</option></select>
                  </div>
                  <div className="overflow-x-auto"><table className="w-full text-sm"><thead className="border-b border-white/10 text-[10px] uppercase tracking-wider text-zinc-500"><tr><th className="px-3 py-3 text-left">Produk</th><th className="px-3 py-3 text-left">Engineer</th><th className="px-3 py-3 text-left">Harga & status</th><th className="px-3 py-3 text-left">Transaksi</th><th className="px-3 py-3 text-right">Tindakan</th></tr></thead><tbody>{filteredEngineerProducts.map((product) => <tr key={product.id} className="border-b border-white/5 align-top"><td className="px-3 py-4"><div className="flex min-w-56 items-center gap-3"><img src={product.image_url} alt="" className="h-12 w-16 rounded-lg bg-black object-cover" /><div><div className="font-semibold">{product.name}</div><div className="mt-1 text-[10px] text-zinc-500">{product.category} · {product.has_download_file ? "File tersedia" : "File belum tersedia"}</div></div></div></td><td className="px-3 py-4"><div className="font-medium">{product.owner_engineer_name || "Engineer"}</div><div className="mt-1 font-mono text-[10px] text-zinc-600">{product.owner_engineer_id}</div></td><td className="px-3 py-4"><div className="font-semibold">{product.is_free ? "Gratis" : fmtPrice(product.price)}</div><span className={`mt-2 inline-flex rounded-full px-2 py-1 text-[9px] font-bold uppercase ${product.status === "published" ? "bg-emerald-500/15 text-emerald-300" : product.status === "archived" ? "bg-zinc-500/15 text-zinc-400" : "bg-amber-500/15 text-amber-300"}`}>{product.status}</span></td><td className="px-3 py-4"><div>{product.sales_count || 0} penjualan</div><div className="mt-1 text-[10px] text-zinc-500">{product.transaction_count || 0} seluruh transaksi</div></td><td className="px-3 py-4 text-right"><div className="inline-flex max-w-xs flex-wrap justify-end gap-2"><button onClick={() => setEngineerProductEdit({ ...product })} disabled={engineerProductActionId === product.id} className="rounded-full border border-white/10 px-3 py-2 text-xs">Edit</button>{product.status === "published" && <button onClick={() => setEngineerProductState(product, "draft")} disabled={engineerProductActionId === product.id} className="rounded-full border border-amber-400/30 px-3 py-2 text-xs text-amber-200">Draft</button>}{product.status === "draft" && <button onClick={() => setEngineerProductState(product, "published")} disabled={engineerProductActionId === product.id || !product.has_download_file} className="rounded-full border border-emerald-400/30 px-3 py-2 text-xs text-emerald-200 disabled:opacity-40">Terbitkan</button>}{product.status === "archived" ? <button onClick={() => setEngineerProductState(product, "draft")} disabled={engineerProductActionId === product.id} className="rounded-full border border-sky-400/30 px-3 py-2 text-xs text-sky-200">Pulihkan</button> : <button onClick={() => setEngineerProductState(product, "archived")} disabled={engineerProductActionId === product.id} className="rounded-full border border-zinc-400/30 px-3 py-2 text-xs text-zinc-300">Arsipkan</button>}{product.can_permanently_delete && <button onClick={() => permanentlyDeleteEngineerProduct(product)} disabled={engineerProductActionId === product.id} className="rounded-full border border-red-400/30 px-3 py-2 text-xs text-red-300">Hapus permanen</button>}</div>{!product.can_permanently_delete && <div className="mt-2 text-[10px] text-zinc-600">Hapus permanen dikunci karena ada riwayat transaksi.</div>}</td></tr>)}</tbody></table>{filteredEngineerProducts.length === 0 && <div className="py-16 text-center text-sm text-zinc-500">Tidak ada produk engineer yang sesuai.</div>}</div>
                </div>
              ) : tab === "engineer-payouts" ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
                      <tr><th className="text-left py-3 px-3">Engineer</th><th className="text-left py-3 px-3">Rekening</th><th className="text-left py-3 px-3">Nominal</th><th className="text-left py-3 px-3">Status</th><th className="text-left py-3 px-3">Tanggal</th><th className="text-right py-3 px-3">Tindakan</th></tr>
                    </thead>
                    <tbody>{items.map((payout) => <tr key={payout.id} className="border-b border-white/5"><td className="py-3 px-3"><div className="font-medium">{payout.engineer_name}</div><div className="text-[10px] text-zinc-500 mt-1">{payout.earning_ids?.length || 0} transaksi</div></td><td className="py-3 px-3 text-xs text-zinc-400"><div>{payout.bank?.bank_name} · {payout.bank?.account_number}</div><div className="text-zinc-600 mt-1">{payout.bank?.account_holder}</div></td><td className="py-3 px-3 font-mono font-semibold">{fmtPrice(payout.amount)}</td><td className="py-3 px-3"><span className={`px-2 py-1 rounded-full text-[10px] uppercase ${payout.status === "paid" ? "bg-emerald-500/15 text-emerald-300" : payout.status === "rejected" ? "bg-rose-500/15 text-rose-300" : "bg-amber-500/15 text-amber-300"}`}>{payout.status}</span>{payout.transfer_reference && <div className="text-[10px] text-zinc-500 mt-1">Ref. {payout.transfer_reference}</div>}</td><td className="py-3 px-3 text-xs text-zinc-500">{new Date(payout.created_at).toLocaleString("id-ID")}</td><td className="py-3 px-3 text-right">{payout.status === "pending" && <div className="inline-flex gap-2"><button onClick={() => processEngineerPayout(payout, "paid")} disabled={payoutActionId === payout.id} className="px-3 py-2 rounded-full bg-emerald-500 text-black text-xs font-semibold disabled:opacity-50">Sudah transfer</button><button onClick={() => processEngineerPayout(payout, "reject")} disabled={payoutActionId === payout.id} className="px-3 py-2 rounded-full border border-rose-400/30 text-rose-200 text-xs disabled:opacity-50">Tolak</button></div>}</td></tr>)}</tbody>
                  </table>
                  {items.length === 0 && <div className="py-16 text-center text-sm text-zinc-500">Belum ada permintaan payout engineer.</div>}
                </div>
              ) : tab === "transactions" ? (
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
                    <tr>
                      <th className="text-left py-3 px-3">Product</th>
                      <th className="text-left py-3 px-3">Amount</th>
                      <th className="text-left py-3 px-3">Method</th>
                      <th className="text-left py-3 px-3">Status</th>
                      <th className="text-left py-3 px-3">Proof</th>
                      <th className="text-left py-3 px-3">Date</th>
                      <th className="text-right py-3 px-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((t) => (
                      <tr key={t.id} className="border-b border-white/5">
                        <td className="py-3 px-3">
                          <div className="font-medium">{t.product_name}</div>
                          <div className="text-[10px] text-zinc-500 mt-1">{t.buyer_name || t.buyer_email || "Guest"}</div>
                        </td>
                        <td className="py-3 px-3 font-mono">{fmtPrice(t.payable_amount || t.amount)}</td>
                        <td className="py-3 px-3 text-xs text-zinc-400">
                          {t.payment_method === "manual_bank"
                            ? "Bank transfer (legacy)"
                            : t.payment_method === "doku"
                              ? "DOKU"
                              : t.payment_method || "Legacy"}
                        </td>
                        <td className="py-3 px-3">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${
                            t.payment_status === "paid"
                              ? "bg-emerald-500/15 text-emerald-400"
                              : t.payment_status === "failed" || t.proof_status === "rejected"
                                ? "bg-red-500/15 text-red-400"
                                : "bg-amber-500/15 text-amber-400"
                          }`}>
                            {t.proof_status === "submitted" ? "verify proof" : t.status || t.payment_status}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          {t.proof_url ? (
                            <a
                              href={t.proof_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1.5 text-xs text-sky-400 hover:text-sky-300"
                            >
                              View <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            <span className="text-xs text-zinc-600">-</span>
                          )}
                        </td>
                        <td className="py-3 px-3 font-mono text-xs text-zinc-400">{(t.created_at || "").slice(0, 10)}</td>
                        <td className="py-3 px-3 text-right">
                          <div className="inline-flex items-center gap-1">
                            {!showArchivedTransactions && t.payment_method === "manual_bank" && t.proof_status === "submitted" && (
                              <>
                              <button
                                onClick={() => reviewManualPayment(t, "approve")}
                                title="Approve payment"
                                className="p-2 rounded-full bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20"
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => reviewManualPayment(t, "reject")}
                                title="Reject proof"
                                className="p-2 rounded-full bg-red-500/10 text-red-400 hover:bg-red-500/20"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                              </>
                            )}
                            <button
                              onClick={() => setTransactionArchived(t, !showArchivedTransactions)}
                              disabled={transactionActionId === t.id}
                              title={showArchivedTransactions ? "Pulihkan transaksi" : "Arsipkan transaksi"}
                              className={`p-2 rounded-full transition-colors disabled:opacity-50 ${
                                showArchivedTransactions
                                  ? "bg-sky-500/10 text-sky-400 hover:bg-sky-500/20"
                                  : "bg-zinc-500/10 text-zinc-400 hover:bg-zinc-500/20"
                              }`}
                            >
                              {transactionActionId === t.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : showArchivedTransactions ? (
                                <RotateCcw className="w-4 h-4" />
                              ) : (
                                <EyeOff className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : tab === "blog" ? (
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
                    <tr>
                      <th className="text-left py-3 px-3">Title</th>
                      <th className="text-left py-3 px-3">Slug</th>
                      <th className="text-left py-3 px-3">Status</th>
                      <th className="text-left py-3 px-3">Updated</th>
                      <th className="text-right py-3 px-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((p) => (
                      <tr key={p.id} className="border-b border-white/5">
                        <td className="py-3 px-3 font-medium">{p.title}</td>
                        <td className="py-3 px-3 text-xs text-zinc-400 font-mono truncate max-w-[180px]">{p.slug}</td>
                        <td className="py-3 px-3">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${
                            p.status === "published" ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-500/15 text-zinc-400"
                          }`}>
                            {p.status}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-xs text-zinc-400 font-mono">{(p.updated_at || p.created_at || "").slice(0, 10)}</td>
                        <td className="py-3 px-3 text-right">
                          <button data-testid={`edit-${p.id}`} onClick={() => openEdit(p)} className="p-2 rounded hover:bg-white/5 mr-1"><Pencil className="w-3.5 h-3.5" /></button>
                          <button data-testid={`delete-${p.id}`} onClick={() => remove(p)} className="p-2 rounded hover:bg-white/5 text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : tab === "coupons" ? (
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
                    <tr>
                      <th className="text-left py-3 px-3">Code</th>
                      <th className="text-left py-3 px-3">Benefit</th>
                      <th className="text-left py-3 px-3">Used</th>
                      <th className="text-left py-3 px-3">Expires</th>
                      <th className="text-left py-3 px-3">Status</th>
                      <th className="text-right py-3 px-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((c) => (
                      <tr key={c.id} className="border-b border-white/5">
                        <td className="py-3 px-3 font-mono font-bold">{c.code}</td>
                        <td className="py-3 px-3 text-xs">
                          {(c.coupon_type || "discount") === "trial"
                            ? `Trial ${c.trial_days || 7} hari · ${
                                products.find((product) => product.id === c.trial_product_id)?.name
                                || "Product"
                              }`
                            : (
                              <>
                                <div>
                                  {c.discount_type === "percent"
                                    ? `${c.discount_value}%`
                                    : fmtPrice(c.discount_value)}
                                </div>
                                <div className="mt-1 text-[10px] text-zinc-500">
                                  {c.discount_product_id
                                    ? products.find((product) => product.id === c.discount_product_id)?.name
                                      || "Specific Product"
                                    : "All Products"}
                                </div>
                              </>
                            )}
                        </td>
                        <td className="py-3 px-3 text-xs text-zinc-400 font-mono">
                          {c.times_used || 0}{c.max_uses ? `/${c.max_uses}` : ""}
                        </td>
                        <td className="py-3 px-3 text-xs text-zinc-400 font-mono">{c.expires_at?.slice(0, 10) || "—"}</td>
                        <td className="py-3 px-3">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${
                            c.active ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-500/15 text-zinc-400"
                          }`}>
                            {c.active ? "active" : "inactive"}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-right">
                          <button data-testid={`edit-${c.id}`} onClick={() => openEdit(c)} className="p-2 rounded hover:bg-white/5 mr-1"><Pencil className="w-3.5 h-3.5" /></button>
                          <button data-testid={`delete-${c.id}`} onClick={() => remove(c)} className="p-2 rounded hover:bg-white/5 text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : licenseTabActive ? (

<div className="space-y-6">

{tab === "licenses" && <form
  onSubmit={createManualLicense}
  className="rounded-xl border border-white/10 bg-black/20 p-4"
>
  <div className="mb-4">
    <h3 className="font-bold">Generate Serial Manual</h3>
    <p className="mt-1 text-xs text-zinc-500">
      Pilih product dan masukkan email customer yang sudah terdaftar. Serial akan dikirim melalui email dan tampil di dashboard customer.
    </p>
  </div>
  <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
    <select
      value={manualLicenseProductId}
      onChange={(event) => setManualLicenseProductId(event.target.value)}
      disabled={creatingLicense}
      className="rounded-lg border border-white/10 bg-zinc-950 px-3 py-2 text-sm outline-none focus:border-[#e11d48] disabled:opacity-50"
      aria-label="Product untuk serial"
      required
    >
      <option value="">Pilih product</option>
      {products.filter((product) => product.requires_license).map((product) => (
        <option key={product.id} value={product.id}>{product.name}</option>
      ))}
    </select>
    <input
      type="email"
      value={manualLicenseEmail}
      onChange={(event) => setManualLicenseEmail(event.target.value)}
      disabled={creatingLicense}
      placeholder="email@customer.com"
      className="rounded-lg border border-white/10 bg-zinc-950 px-3 py-2 text-sm outline-none placeholder:text-zinc-600 focus:border-[#e11d48] disabled:opacity-50"
      aria-label="Email customer"
      required
    />
    <button
      type="submit"
      disabled={creatingLicense}
      className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#e11d48] px-4 py-2 text-sm font-semibold transition hover:bg-[#be123c] disabled:cursor-not-allowed disabled:opacity-50"
    >
      {creatingLicense ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
      {creatingLicense ? "Membuat…" : "Generate Serial"}
    </button>
  </div>
</form>}

{tab === "vst-licenses" && (
  <form
    onSubmit={grantVstBusinessPlan}
    className="rounded-2xl border border-[#fb7185]/20 bg-gradient-to-br from-[#e11d48]/[0.08] to-black/20 p-5"
    data-testid="vst-plan-grant-form"
  >
    <div className="flex items-start gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#fb7185]/25 bg-[#e11d48]/15 text-[#fb7185]"><Gift className="h-5 w-5" /></div>
      <div>
        <h3 className="font-bold text-white">Berikan Paket VST Gratis</h3>
        <p className="mt-1 text-xs leading-5 text-zinc-500">Berikan paket Personal, Creator, Creator Commercial, atau Studio Commercial kepada akun customer yang sudah terdaftar. Pemberian tersimpan sebagai entitlement admin dan belum terikat ke project.</p>
      </div>
    </div>
    <div className="mt-5 grid gap-3 lg:grid-cols-[1fr_1fr_1.15fr_auto]">
      <input
        type="email"
        value={vstPlanGrantEmail}
        onChange={(event) => setVstPlanGrantEmail(event.target.value)}
        disabled={grantingVstPlan}
        placeholder="email@customer.com"
        className="rounded-xl border border-white/10 bg-zinc-950 px-3 py-3 text-sm outline-none placeholder:text-zinc-700 focus:border-[#e11d48] disabled:opacity-50"
        aria-label="Email penerima paket VST"
        data-testid="vst-plan-grant-email"
        required
      />
      <select
        value={vstPlanGrantId}
        onChange={(event) => setVstPlanGrantId(event.target.value)}
        disabled={grantingVstPlan}
        className="rounded-xl border border-white/10 bg-zinc-950 px-3 py-3 text-sm outline-none focus:border-[#e11d48] disabled:opacity-50"
        aria-label="Paket VST gratis"
        data-testid="vst-plan-grant-plan"
      >
        {vstPlanGrantOptions.map((plan) => <option key={plan.id} value={plan.id}>{plan.label} — {plan.detail}</option>)}
      </select>
      <input
        type="text"
        value={vstPlanGrantNote}
        onChange={(event) => setVstPlanGrantNote(event.target.value)}
        disabled={grantingVstPlan}
        maxLength={500}
        placeholder="Catatan internal (opsional)"
        className="rounded-xl border border-white/10 bg-zinc-950 px-3 py-3 text-sm outline-none placeholder:text-zinc-700 focus:border-[#e11d48] disabled:opacity-50"
        aria-label="Catatan pemberian paket"
        data-testid="vst-plan-grant-note"
      />
      <button
        type="submit"
        disabled={grantingVstPlan}
        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-[#e11d48] px-5 py-3 text-sm font-bold text-white transition hover:bg-[#be123c] disabled:cursor-not-allowed disabled:opacity-50"
        data-testid="vst-plan-grant-submit"
      >
        {grantingVstPlan ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
        {grantingVstPlan ? "Memberikan…" : "Berikan paket"}
      </button>
    </div>
  </form>
)}

{tab === "vst-licenses" && (
  <div className="rounded-xl border border-sky-400/15 bg-sky-400/[0.05] px-4 py-3">
    <h3 className="text-sm font-bold text-sky-200">Lisensi Biasa</h3>
    <p className="mt-0.5 text-[10px] text-zinc-500">Serial pribadi yang dibuat bersama build VST untuk pemilik project.</p>
  </div>
)}

{groupedLicenseItems.length === 0 && (
  <div className="rounded-xl border border-dashed border-white/10 px-4 py-10 text-center text-sm text-zinc-500">
    {normalizedLicenseSearch
      ? `Tidak ada ${tab === "vst-licenses" ? "lisensi biasa" : "license"} untuk pencarian “${licenseSearch.trim()}”.`
      : tab === "vst-licenses" ? "Belum ada lisensi biasa." : "Belum ada license."}
  </div>
)}

{Object.entries(groupedLicenses).map(([product, licenses]) => {

const fullLicenses = licenses.filter(
  (l) => l.license_type !== "trial"
);

const trialLicenses = licenses.filter(
  (l) => l.license_type === "trial"
);

return (

<div
key={product}
className="border border-white/10 rounded-xl overflow-hidden"
>

<button
onClick={() => toggleLicenseGroup(product)}
className="w-full flex items-center justify-between px-3 py-1.5 bg-[#e11d48] hover:bg-[#be123c] text-white rounded-t-xl transition"
>

<div>

<h3 className="font-bold text-xs leading-tight">

🎹 {product}

</h3>

<p className="text-[9px] text-white/75">
  
{licenses.length} {licenses.length === 1 ? "License" : "Licenses"}
  
</p>

</div>

<div className="text-base leading-none">

{licenseGroups[product] === false ? "+" : "−"}

</div>

</button>

{licenseGroups[product] !== false && (

<table className="w-full text-sm">

<thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">

<tr>

<th className="text-left py-3 px-3">License Key</th>

<th className="text-left py-3 px-3">Customer</th>

<th className="text-left py-3 px-3">HW ID</th>

<th className="text-left py-3 px-3">Status</th>

<th className="text-right py-3 px-3">Actions</th>

</tr>

</thead>

<tbody>

{/* FULL LICENSE */}
{fullLicenses.length > 0 && (
  <>
    <tr className="bg-emerald-900/20">
      <td
        colSpan={5}
        className="px-4 py-2 font-semibold text-emerald-400"
      >
        🟢 {tab === "vst-licenses" ? "Lisensi VST Maker" : "Lisensi Penuh"} ({fullLicenses.length})
      </td>
    </tr>

    {fullLicenses.map((l) => (

      <tr
        key={l.id}
        className="border-b border-white/5"
      >

        <td className="py-3 px-3 font-mono text-xs">
          {l.license_key}
        </td>

        <td className="py-3 px-3 text-xs">
          <div>{l.customer_name}</div>
          <div className="text-zinc-500">
            {l.customer_email}
          </div>
          <div className="mt-1 text-[9px] font-semibold uppercase tracking-wide text-sky-400">
            {l.source === "vst_maker_distribution" ? "VST Maker · Distribusi" : l.source === "vst_maker_build" ? "VST Maker · Build" : "Produk toko"}
          </div>
        </td>

        <td className="py-3 px-3 font-mono text-[10px] text-zinc-400 truncate max-w-[120px]">
          {l.hardware_id || "—"}
        </td>

        <td className="py-3 px-3">

          <span
            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${
              l.status === "active"
                ? "bg-emerald-500/15 text-emerald-400"
                : l.status === "revoked"
                ? "bg-red-500/15 text-red-400"
                : "bg-zinc-500/15 text-zinc-400"
            }`}
          >

            {l.status}

          </span>

        </td>

        <td className="py-3 px-3 text-right whitespace-nowrap">

          <button
            onClick={() => resetLicense(l)}
            title="Reset"
            disabled={!['active', 'unactivated'].includes(l.status)}
            className="p-2 rounded hover:bg-white/5 mr-1 disabled:cursor-not-allowed disabled:opacity-25"
          >
            <RotateCcw className="w-3.5 h-3.5"/>
          </button>

          <button
            onClick={() => revokeLicense(l)}
            title="Revoke"
            className="p-2 rounded hover:bg-white/5 mr-1 text-amber-400"
          >
            <Ban className="w-3.5 h-3.5"/>
          </button>

          <button
            onClick={() => deleteLicense(l)}
            title="Delete"
            className="p-2 rounded hover:bg-white/5 text-red-400"
          >
            <Trash2 className="w-3.5 h-3.5"/>
          </button>

        </td>

      </tr>

    ))}

  </>
)}

{/* TRIAL LICENSE */}

{trialLicenses.length > 0 && (
  <>
    <tr className="bg-yellow-900/20">
      <td
        colSpan={5}
        className="px-4 py-2 font-semibold text-yellow-400"
      >
        🟡 Trial License ({trialLicenses.length})
      </td>
    </tr>

    {trialLicenses.map((l) => (

      <tr
        key={l.id}
        className="border-b border-white/5"
      >

        <td className="py-3 px-3 font-mono text-xs">
          {l.license_key}
        </td>

        <td className="py-3 px-3 text-xs">
          <div>{l.customer_name}</div>
          <div className="text-zinc-500">
            {l.customer_email}
          </div>
        </td>

        <td className="py-3 px-3 font-mono text-[10px] text-zinc-400 truncate max-w-[120px]">
          {l.hardware_id || "—"}
        </td>

        <td className="py-3 px-3">

          <span
            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${
              l.status === "active"
                ? "bg-emerald-500/15 text-emerald-400"
                : l.status === "revoked"
                ? "bg-red-500/15 text-red-400"
                : "bg-zinc-500/15 text-zinc-400"
            }`}
          >

            {l.status}

          </span>

        </td>

        <td className="py-3 px-3 text-right whitespace-nowrap">

          <button
            onClick={() => resetLicense(l)}
            title="Reset"
            disabled={!['active', 'unactivated'].includes(l.status)}
            className="p-2 rounded hover:bg-white/5 mr-1 disabled:cursor-not-allowed disabled:opacity-25"
          >
            <RotateCcw className="w-3.5 h-3.5"/>
          </button>

          <button
            onClick={() => revokeLicense(l)}
            title="Revoke"
            className="p-2 rounded hover:bg-white/5 mr-1 text-amber-400"
          >
            <Ban className="w-3.5 h-3.5"/>
          </button>

          <button
            onClick={() => deleteLicense(l)}
            title="Delete"
            className="p-2 rounded hover:bg-white/5 text-red-400"
          >
            <Trash2 className="w-3.5 h-3.5"/>
          </button>

        </td>

      </tr>

    ))}

  </>
)}

</tbody>

</table>

)}

</div>

);

})}

{tab === "vst-licenses" && (
  <div className="space-y-3 border-t border-white/10 pt-6">
    <div className="rounded-xl border border-amber-400/15 bg-amber-400/[0.05] px-4 py-3">
      <h3 className="text-sm font-bold text-amber-200">Lisensi Distribusi / Dijual Kembali</h3>
      <p className="mt-0.5 text-[10px] text-zinc-500">Paket serial yang dibeli pemilik VST untuk diterbitkan kembali kepada pembelinya.</p>
    </div>

    {filteredVstDistributions.length === 0 && (
      <div className="rounded-xl border border-dashed border-white/10 px-4 py-8 text-center text-sm text-zinc-500">
        {normalizedLicenseSearch ? "Tidak ada paket distribusi yang cocok dengan pencarian." : "Belum ada pembelian lisensi distribusi."}
      </div>
    )}

    {filteredVstDistributions.map((plan) => {
      const groupKey = `distribution:${plan.id}`;
      const issuedLicenses = Array.isArray(plan.licenses) ? plan.licenses : [];
      return (
        <div key={plan.id} className="overflow-hidden rounded-xl border border-amber-300/15">
          <button
            type="button"
            onClick={() => toggleLicenseGroup(groupKey)}
            className="flex w-full items-center justify-between bg-amber-400/90 px-3 py-1.5 text-left text-zinc-950 transition hover:bg-amber-300"
          >
            <div className="min-w-0">
              <h3 className="truncate text-xs font-black leading-tight">{plan.product_name || "VST Tanpa Nama"}</h3>
              <p className="truncate text-[9px] font-semibold text-zinc-800/75">
                Reseller: {plan.owner_customer_name || "Customer"} · {plan.owner_customer_email || "email tidak tersedia"}
              </p>
            </div>
            <div className="ml-3 flex shrink-0 items-center gap-2 text-[9px] font-bold">
              <span>{Number(plan.quota_issued) || 0} terbit</span>
              <span>{Number(plan.quota_remaining) || 0} tersisa</span>
              <span className="text-base leading-none">{licenseGroups[groupKey] === false ? "+" : "−"}</span>
            </div>
          </button>

          {licenseGroups[groupKey] !== false && (
            <div>
              <div className="grid gap-2 border-b border-white/10 bg-black/20 px-3 py-2 text-[10px] text-zinc-400 sm:grid-cols-4">
                <span>Dibeli <strong className="text-white">{Number(plan.quota_purchased) || 0}</strong></span>
                <span>Diterbitkan <strong className="text-white">{Number(plan.quota_issued) || 0}</strong></span>
                <span>Tersisa <strong className="text-white">{Number(plan.quota_remaining) || 0}</strong></span>
                <span>Kapasitas <strong className="text-white">{Number(plan.max_activations) || 1} komputer</strong></span>
              </div>
              {issuedLicenses.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-zinc-500">Paket sudah dibeli, tetapi belum ada serial pembeli yang diterbitkan.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="border-b border-white/10 text-[10px] uppercase tracking-wider text-zinc-500">
                    <tr>
                      <th className="px-3 py-3 text-left">License Key</th>
                      <th className="px-3 py-3 text-left">Pembeli</th>
                      <th className="px-3 py-3 text-left">HW ID</th>
                      <th className="px-3 py-3 text-left">Status</th>
                      <th className="px-3 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issuedLicenses.map((license) => (
                      <tr key={license.id} className="border-b border-white/5">
                        <td className="px-3 py-3 font-mono text-xs">{license.license_key}</td>
                        <td className="px-3 py-3 text-xs">
                          <div>{license.customer_name || "Customer"}</div>
                          <div className="text-zinc-500">{license.customer_email || "—"}</div>
                        </td>
                        <td className="max-w-[120px] truncate px-3 py-3 font-mono text-[10px] text-zinc-400">{license.hardware_id || "—"}</td>
                        <td className="px-3 py-3">
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${license.status === "active" ? "bg-emerald-500/15 text-emerald-400" : license.status === "revoked" ? "bg-red-500/15 text-red-400" : "bg-zinc-500/15 text-zinc-400"}`}>{license.status}</span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-right">
                          <button onClick={() => resetLicense(license)} title="Reset" disabled={!['active', 'unactivated'].includes(license.status)} className="mr-1 rounded p-2 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-25"><RotateCcw className="h-3.5 w-3.5" /></button>
                          <button onClick={() => revokeLicense(license)} title="Revoke" className="mr-1 rounded p-2 text-amber-400 hover:bg-white/5"><Ban className="h-3.5 w-3.5" /></button>
                          <button onClick={() => deleteLicense(license)} title="Delete" className="rounded p-2 text-red-400 hover:bg-white/5"><Trash2 className="h-3.5 w-3.5" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      );
    })}
  </div>
)}

</div>

              ) : tab === "customers" ? (
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
                    <tr>
                      <th className="text-left py-3 px-3">Name</th>
                      <th className="text-left py-3 px-3">Email</th>
                      <th className="text-left py-3 px-3">Phone</th>
                      <th className="text-left py-3 px-3">Marketing</th>
                      <th className="text-left py-3 px-3">Joined</th>
                      <th className="text-left py-3 px-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCustomers.map((c) => (
                      <tr key={c.id} data-testid={`customer-row-${c.id}`} className="border-b border-white/5">
                        <td className="py-3 px-3 font-medium">{c.name}</td>
                        <td className="py-3 px-3 text-xs text-zinc-400">{c.email || "—"}</td>
                        <td className="py-3 px-3 text-xs text-zinc-400 font-mono">{c.phone || "—"}</td>
                        <td className="py-3 px-3">
                          <span className={`rounded-full px-2 py-1 text-[10px] font-semibold uppercase ${c.marketing_consent ? "bg-emerald-500/15 text-emerald-400" : c.marketing_unsubscribed_at ? "bg-amber-500/15 text-amber-300" : "bg-zinc-500/15 text-zinc-500"}`}>
                            {c.marketing_consent ? "Opted in" : c.marketing_unsubscribed_at ? "Unsubscribed" : "Not subscribed"}
                          </span>
                          {c.marketing_unsubscribed_at && (
                            <div className="mt-1 text-[10px] text-zinc-500" title={c.marketing_unsubscribe_reason || "unsubscribe"}>
                              {new Date(c.marketing_unsubscribed_at).toLocaleString("id-ID")}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-3 font-mono text-xs text-zinc-400">{(c.created_at || "").slice(0, 10)}</td>
                        <td className="py-3 px-3">
                          <div className="flex flex-wrap items-center gap-3">
                            <button onClick={() => openCustomerProducts(c)} className="text-sky-400 hover:underline">Kelola produk</button>
                            <button
                              type="button"
                              data-testid={`email-customer-${c.id}`}
                              onClick={() => openCustomerEmail(c)}
                              disabled={!c.email}
                              className="inline-flex items-center gap-1 text-[#fb7185] hover:underline disabled:cursor-not-allowed disabled:text-zinc-600"
                              title={c.email ? `Email ${c.name}` : "Customer tidak memiliki email"}
                            >
                              <Mail className="h-3.5 w-3.5" /> Email
                            </button>
                            <button
                              type="button"
                              data-testid={`delete-customer-${c.id}`}
                              onClick={() => deleteCustomer(c)}
                              disabled={deletingCustomer === c.id}
                              className="inline-flex items-center gap-1 text-red-400 hover:underline disabled:cursor-wait disabled:text-zinc-600"
                              title={`Hapus akun ${c.name}`}
                            >
                              {deletingCustomer === c.id
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <Trash2 className="h-3.5 w-3.5" />}
                              Hapus akun
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredCustomers.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-3 py-10 text-center text-sm text-zinc-500">
                          {normalizedCustomerSearch
                            ? `Tidak ada customer untuk nama atau email “${customerSearch.trim()}”.`
                            : "Belum ada customer."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
                    <tr>
                      <th className="text-left py-3 px-3">Item</th>
                      <th className="text-left py-3 px-3">Details</th>
                      <th className="text-right py-3 px-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.id} className="border-b border-white/5">
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-3">
                            <img src={it.cover_url || it.image_url} alt="" className="w-10 h-10 rounded object-cover" />
                            <div className="font-semibold">{it.title || it.name}</div>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-xs text-zinc-400">
                          {tab === "songs" && `${it.artist} · ${it.genre}`}
                          {tab === "gear" && `${it.brand} · ${it.category}`}
                          {tab === "products" && (
                            <div className="space-y-1">
                              <div>{`${it.category} · ${fmtPrice(it.price)} · ${it.requires_license ? `${it.max_activations || 1} PC · Trial ${it.trial_enabled !== false ? `${it.trial_days || 7}d` : "Off"}` : "No license"}`}</div>
                              {it.upgrade_enabled && (
                                <div className="text-emerald-400">
                                  Upgrade: {it.upgrade_discount_type === "percent" ? `${it.upgrade_discount_value}%` : fmtPrice(it.upgrade_discount_value)} · {it.upgrade_from_product_ids?.length || 0} produk asal
                                </div>
                              )}
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${
                                  (it.status || "published") === "published"
                                    ? "bg-emerald-500/15 text-emerald-400"
                                    : "bg-amber-500/15 text-amber-400"
                                }`}>
                                  {it.status || "published"}
                                </span>
                                <button type="button" onClick={() => copyProductId(it)} className="inline-flex items-center gap-1 font-mono text-[10px] text-zinc-500 hover:text-white" title="Copy Product ID">
                                  <Copy className="w-3 h-3" /> {it.id}
                                </button>
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-3 text-right">
                          {tab === "products" && (it.status || "published") === "published" && (
                            <button data-testid={`unpublish-${it.id}`} onClick={() => setProductPublication(it, false)} title="Move to Draft" className="p-2 rounded hover:bg-white/5 mr-1 text-amber-400"><EyeOff className="w-3.5 h-3.5" /></button>
                          )}
                          {tab === "products" && (it.status || "published") !== "published" && (
                            <button data-testid={`publish-${it.id}`} onClick={() => setProductPublication(it, true)} title="Publish product" className="p-2 rounded hover:bg-white/5 mr-1 text-emerald-400"><Eye className="w-3.5 h-3.5" /></button>
                          )}
                          <button data-testid={`edit-${it.id}`} onClick={() => openEdit(it)} className="p-2 rounded hover:bg-white/5 mr-1"><Pencil className="w-3.5 h-3.5" /></button>
                          <button data-testid={`delete-${it.id}`} onClick={() => remove(it)} className="p-2 rounded hover:bg-white/5 text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {tab !== "security" && tab !== "engineer-products" && items.length === 0 && (
                <div className="text-center py-12 text-zinc-500">No items.</div>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* Modal */}

      {selectedEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur" role="dialog" aria-modal="true" aria-label="Email masuk">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-white/10 bg-[#0a0a0c] p-6 sm:p-8">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#fb7185]">Incoming Email</div>
                <h2 className="break-words text-2xl font-bold">{selectedEmail.subject || "(No subject)"}</h2>
              </div>
              <button type="button" onClick={() => setSelectedEmail(null)} className="shrink-0 rounded p-2 hover:bg-white/5" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>

            <dl className="mb-6 grid gap-3 rounded-xl border border-white/10 bg-black/20 p-4 text-sm sm:grid-cols-[90px_1fr]">
              <dt className="text-zinc-500">From</dt>
              <dd className="break-all text-zinc-200">{selectedEmail.from || "—"}</dd>
              <dt className="text-zinc-500">To</dt>
              <dd className="break-all text-zinc-200">{selectedEmail.to?.join(", ") || "—"}</dd>
              <dt className="text-zinc-500">Received</dt>
              <dd className="text-zinc-200">{selectedEmail.received_at ? new Date(selectedEmail.received_at).toLocaleString("id-ID") : "—"}</dd>
            </dl>

            {loadingEmail ? (
              <div className="flex min-h-48 items-center justify-center text-zinc-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading email...</div>
            ) : (
              <>
                {selectedEmail.content_error && (
                  <div className="mb-4 rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-sm text-amber-200">{selectedEmail.content_error}</div>
                )}
                <div className="min-h-48 whitespace-pre-wrap break-words rounded-xl border border-white/10 bg-white p-5 text-sm leading-6 text-zinc-900">
                  {selectedEmail.display_text || selectedEmail.text || "Isi email belum tersedia."}
                </div>
                {!!selectedEmail.attachments?.length && (
                  <div className="mt-5">
                    <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Attachments</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedEmail.attachments.map((attachment) => (
                        <span key={attachment.id || attachment.filename} className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-xs text-zinc-300">
                          <Paperclip className="h-3.5 w-3.5" /> {attachment.filename || "Attachment"}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {customerEmailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur" role="dialog" aria-modal="true" aria-label="Email customer">
          <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-[#0a0a0c] p-8">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold">Email Customer</h2>
                <p className="mt-1 text-sm text-zinc-400">
                  {customerEmailModal.name} · {customerEmailModal.email}
                </p>
              </div>
              <button type="button" onClick={closeCustomerEmail} disabled={sendingCustomerEmail} className="rounded p-2 hover:bg-white/5 disabled:opacity-50" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <input
                data-testid="customer-email-subject"
                value={customerEmailSubject}
                onChange={(event) => setCustomerEmailSubject(event.target.value)}
                maxLength={200}
                placeholder="Subject"
                className="w-full rounded-lg border border-white/10 bg-[#050505] px-4 py-3"
              />
              <textarea
                data-testid="customer-email-message"
                rows={10}
                value={customerEmailMessage}
                onChange={(event) => setCustomerEmailMessage(event.target.value)}
                maxLength={20000}
                placeholder="Write your email..."
                className="w-full rounded-lg border border-white/10 bg-[#050505] px-4 py-3"
              />
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={closeCustomerEmail} disabled={sendingCustomerEmail} className="rounded-full border border-white/10 px-5 py-2 disabled:opacity-50">Cancel</button>
              <button
                type="button"
                data-testid="customer-email-send"
                onClick={sendCustomerEmail}
                disabled={sendingCustomerEmail}
                className="rounded-full bg-[#e11d48] px-5 py-2 font-semibold disabled:opacity-50"
              >
                {sendingCustomerEmail ? "Sending..." : "Send Email"}
              </button>
            </div>
          </div>
        </div>
      )}

      {engineerProductEdit && <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Edit produk engineer"><form onSubmit={saveEngineerProduct} className="my-8 w-full max-w-2xl rounded-3xl border border-white/10 bg-[#0a0a0c] p-6 md:p-8"><div className="flex items-center justify-between gap-4"><div><div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#fb7185]">{engineerProductEdit.owner_engineer_name}</div><h2 className="mt-1 font-[Outfit] text-2xl font-bold">Edit Produk Engineer</h2></div><button type="button" onClick={() => setEngineerProductEdit(null)} disabled={engineerProductActionId === engineerProductEdit.id} className="rounded-full p-2 hover:bg-white/5 disabled:opacity-40"><X className="h-5 w-5" /></button></div><div className="mt-6 grid gap-4 md:grid-cols-2"><label className="text-xs text-zinc-400">Nama produk<input required minLength={3} maxLength={160} value={engineerProductEdit.name || ""} onChange={(event) => setEngineerProductEdit({ ...engineerProductEdit, name: event.target.value })} className="mt-2 w-full rounded-xl border border-white/10 bg-black p-3 text-sm text-white" /></label><label className="text-xs text-zinc-400">Kategori<select value={engineerProductEdit.category || "Sample Pack"} onChange={(event) => setEngineerProductEdit({ ...engineerProductEdit, category: event.target.value })} className="mt-2 w-full rounded-xl border border-white/10 bg-black p-3 text-sm text-white">{engineerProductCategories.map((category) => <option key={category}>{category}</option>)}</select></label><label className="text-xs text-zinc-400">Harga (IDR)<input required type="text" inputMode="numeric" pattern="[0-9.]*" value={formatIdrInput(engineerProductEdit.price)} onChange={(event) => setEngineerProductEdit({ ...engineerProductEdit, price: normalizeIdrInput(event.target.value) })} className="mt-2 w-full rounded-xl border border-white/10 bg-black p-3 text-sm text-white" placeholder="0" /></label><label className="text-xs text-zinc-400">Nama lisensi<input required minLength={3} maxLength={120} value={engineerProductEdit.license_name || ""} onChange={(event) => setEngineerProductEdit({ ...engineerProductEdit, license_name: event.target.value })} className="mt-2 w-full rounded-xl border border-white/10 bg-black p-3 text-sm text-white" /></label><label className="md:col-span-2 text-xs text-zinc-400">URL cover<input required type="url" value={engineerProductEdit.image_url || ""} onChange={(event) => setEngineerProductEdit({ ...engineerProductEdit, image_url: event.target.value })} className="mt-2 w-full rounded-xl border border-white/10 bg-black p-3 text-sm text-white" /></label><label className="md:col-span-2 text-xs text-zinc-400">Deskripsi <span className="text-zinc-600">(minimal 20 karakter)</span><textarea required minLength={20} maxLength={10000} value={engineerProductEdit.description || ""} onChange={(event) => setEngineerProductEdit({ ...engineerProductEdit, description: event.target.value })} className="mt-2 min-h-28 w-full rounded-xl border border-white/10 bg-black p-3 text-sm text-white" /></label><label className="md:col-span-2 text-xs text-zinc-400">Ketentuan lisensi<textarea maxLength={5000} value={engineerProductEdit.license_terms || ""} onChange={(event) => setEngineerProductEdit({ ...engineerProductEdit, license_terms: event.target.value })} className="mt-2 min-h-20 w-full rounded-xl border border-white/10 bg-black p-3 text-sm text-white" /></label><label className="text-xs text-zinc-400">Preview audio URL<input type="url" value={engineerProductEdit.preview_audio_url || ""} onChange={(event) => setEngineerProductEdit({ ...engineerProductEdit, preview_audio_url: event.target.value })} className="mt-2 w-full rounded-xl border border-white/10 bg-black p-3 text-sm text-white" /></label><label className="text-xs text-zinc-400">Preview YouTube URL<input type="url" value={engineerProductEdit.preview_youtube_url || ""} onChange={(event) => setEngineerProductEdit({ ...engineerProductEdit, preview_youtube_url: event.target.value })} className="mt-2 w-full rounded-xl border border-white/10 bg-black p-3 text-sm text-white" /></label></div><div className="mt-7 flex justify-end gap-2"><button type="button" onClick={() => setEngineerProductEdit(null)} disabled={engineerProductActionId === engineerProductEdit.id} className="rounded-full border border-white/10 px-5 py-2.5 text-sm">Batal</button><button disabled={engineerProductActionId === engineerProductEdit.id} className="rounded-full bg-[#e11d48] px-6 py-2.5 text-sm font-semibold disabled:opacity-40">{engineerProductActionId === engineerProductEdit.id ? "Menyimpan..." : "Simpan perubahan"}</button></div></form></div>}

      {broadcastModal && (

<div className="fixed inset-0 bg-black/80 backdrop-blur flex items-center justify-center z-50 p-4">

<div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0a0a0c] border border-white/10 rounded-2xl p-8">

<h2 className="text-2xl font-bold mb-6">

Broadcast Email

</h2>

<div className="space-y-4">

<div className="space-y-2">
  <label className="text-sm text-gray-400">Campaign Type</label>
  <div className="grid gap-2 sm:grid-cols-2">
    <button
      type="button"
      onClick={() => setBroadcastCampaignType("operational")}
      className={`rounded-xl border p-3 text-left transition-colors ${broadcastCampaignType === "operational" ? "border-sky-400/60 bg-sky-400/10" : "border-white/10 bg-[#050505]"}`}
    >
      <span className="block text-sm font-semibold text-white">Operational Notice</span>
      <span className="mt-1 block text-xs text-zinc-400">Important account, product, payment, license, or service information.</span>
    </button>
    <button
      type="button"
      onClick={() => setBroadcastCampaignType("marketing")}
      className={`rounded-xl border p-3 text-left transition-colors ${broadcastCampaignType === "marketing" ? "border-amber-400/60 bg-amber-400/10" : "border-white/10 bg-[#050505]"}`}
    >
      <span className="block text-sm font-semibold text-white">Marketing Campaign</span>
      <span className="mt-1 block text-xs text-zinc-400">Promotions and product campaigns; consented members only.</span>
    </button>
  </div>
  {broadcastCampaignType === "marketing" && (
    <p className="rounded-lg border border-amber-400/20 bg-amber-400/5 px-3 py-2 text-xs text-amber-200">
      Existing members without recorded marketing consent will be excluded automatically.
    </p>
  )}
  {emailCampaignSettings && (
    <p className="text-xs text-zinc-500">
      Sender: <span className="text-zinc-300">{broadcastCampaignType === "marketing" ? emailCampaignSettings.marketing_sender : emailCampaignSettings.transactional_sender}</span>
    </p>
  )}
</div>

{emailCampaignSettings && !emailCampaignSettings.tracking_webhook_configured && (
  <div className="rounded-xl border border-amber-400/20 bg-amber-400/5 px-4 py-3 text-xs leading-relaxed text-amber-200">
    Delivery tracking setup is pending. Campaign sending works, but delivered, bounced, opened, and clicked will remain zero until the Resend event webhook is connected.
  </div>
)}

<label className="text-sm text-gray-400">
Target Recipient
</label>

<select
value={broadcastTarget}
onChange={(e)=>setBroadcastTarget(e.target.value)}
className="w-full bg-[#050505] border border-white/10 rounded-lg px-4 py-3"
>

<option value="all">
All Customers
</option>

<option value="trial">
Trial Users
</option>

<option value="paid">
Paid Customers
</option>

<option value="license">
Full License Users
</option>

<option disabled>
──────── Products ────────
</option>

{products.map((p)=>(
<option
key={p.id}
value={`product:${p.id}`}
>
Product: {p.name}
</option>
))}

</select>

<div className="space-y-2">
  <div>
    <label className="text-sm text-gray-400">Product Exclusions</label>
    <p className="mt-1 text-xs text-zinc-500">
      Customers who own any selected product will not receive this email.
    </p>
  </div>
  <div className="max-h-44 overflow-y-auto rounded-lg border border-white/10 bg-[#050505] p-3">
    {products.length === 0 ? (
      <p className="text-sm text-zinc-500">No products available.</p>
    ) : products.map((product) => (
      <label key={product.id} className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-white/5">
        <input
          type="checkbox"
          checked={broadcastExcludedProductIds.includes(product.id)}
          onChange={(event) => setBroadcastExcludedProductIds((current) => (
            event.target.checked
              ? [...current, product.id]
              : current.filter((id) => id !== product.id)
          ))}
          className="h-4 w-4 accent-[#e11d48]"
        />
        <span className="text-sm text-zinc-200">{product.name}</span>
      </label>
    ))}
  </div>
  {broadcastExcludedProductIds.length > 0 && (
    <p className="text-xs text-amber-300">
      {broadcastExcludedProductIds.length} product exclusion{broadcastExcludedProductIds.length > 1 ? "s" : ""} selected
    </p>
  )}
</div>

<div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
  <div className="flex flex-wrap items-center justify-between gap-3">
    <div>
      <p className="text-sm font-semibold text-white">Recipient Preview</p>
      <p className="mt-1 text-xs text-zinc-500">Required before the final send.</p>
    </div>
    <button
      type="button"
      onClick={previewBroadcastRecipients}
      disabled={previewingBroadcast || sendingBroadcast}
      className="rounded-full border border-white/15 px-4 py-2 text-sm font-semibold disabled:opacity-50"
    >
      {previewingBroadcast ? "Calculating..." : "Preview Recipients"}
    </button>
  </div>
  {broadcastPreview && (
    <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
      <div className="rounded-lg bg-black/30 p-3"><p className="text-xs text-zinc-500">Targeted</p><p className="mt-1 text-xl font-bold">{broadcastPreview.targeted}</p></div>
      <div className="rounded-lg bg-black/30 p-3"><p className="text-xs text-zinc-500">No consent</p><p className="mt-1 text-xl font-bold text-amber-300">{broadcastPreview.consent_excluded}</p></div>
      <div className="rounded-lg bg-black/30 p-3"><p className="text-xs text-zinc-500">Suppressed</p><p className="mt-1 text-xl font-bold text-amber-300">{broadcastPreview.suppression_excluded || 0}</p></div>
      <div className="rounded-lg bg-black/30 p-3"><p className="text-xs text-zinc-500">Product excluded</p><p className="mt-1 text-xl font-bold text-amber-300">{broadcastPreview.product_excluded}</p></div>
      <div className="rounded-lg bg-emerald-400/10 p-3"><p className="text-xs text-emerald-300">Will receive</p><p className="mt-1 text-xl font-bold text-emerald-300">{broadcastPreview.recipients}</p></div>
    </div>
  )}
</div>


<input
value={broadcastSubject}
onChange={(e)=>setBroadcastSubject(e.target.value)}
placeholder="Subject"
className="w-full bg-[#050505] border border-white/10 rounded-lg px-4 py-3"
/>


<textarea
rows={10}
value={broadcastMessage}
onChange={(e)=>setBroadcastMessage(e.target.value)}
placeholder="Write your email..."
className="w-full bg-[#050505] border border-white/10 rounded-lg px-4 py-3"
/>

<div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
  <label className="text-sm font-semibold text-white">Delivery Time</label>
  <div className="mt-3 grid gap-2 sm:grid-cols-2">
    <button
      type="button"
      onClick={() => setBroadcastScheduleMode("now")}
      className={`rounded-lg border px-4 py-3 text-left text-sm ${broadcastScheduleMode === "now" ? "border-[#e11d48] bg-[#e11d48]/10" : "border-white/10 bg-[#050505]"}`}
    >
      <span className="block font-semibold">Send now</span>
      <span className="mt-1 block text-xs text-zinc-500">Start after the campaign enters the queue.</span>
    </button>
    <button
      type="button"
      onClick={() => setBroadcastScheduleMode("scheduled")}
      className={`rounded-lg border px-4 py-3 text-left text-sm ${broadcastScheduleMode === "scheduled" ? "border-[#e11d48] bg-[#e11d48]/10" : "border-white/10 bg-[#050505]"}`}
    >
      <span className="block font-semibold">Schedule</span>
      <span className="mt-1 block text-xs text-zinc-500">Use the date and time from this device.</span>
    </button>
  </div>
  {broadcastScheduleMode === "scheduled" && (
    <input
      type="datetime-local"
      data-testid="broadcast-scheduled-at"
      value={broadcastScheduledAt}
      onChange={(event) => setBroadcastScheduledAt(event.target.value)}
      className="mt-3 w-full rounded-lg border border-white/10 bg-[#050505] px-4 py-3 text-sm [color-scheme:dark] focus:border-[#e11d48] focus:outline-none"
    />
  )}
</div>


</div>

<div className="flex flex-wrap justify-end gap-3 mt-6">

<button
onClick={() => {
  setBroadcastModal(false);
  setBroadcastExcludedProductIds([]);
  setBroadcastScheduleMode("now");
  setBroadcastScheduledAt("");
}}
disabled={sendingBroadcast || sendingBroadcastTest}
className="px-5 py-2 rounded-full border border-white/10"
>

Cancel

</button>

<button
type="button"
onClick={sendBroadcastTest}
disabled={sendingBroadcast || sendingBroadcastTest}
className="px-5 py-2 rounded-full border border-white/10 disabled:opacity-50"
>
{sendingBroadcastTest ? "Sending Test..." : "Send Test"}
</button>

<button
onClick={sendBroadcastEmail}
disabled={sendingBroadcast || sendingBroadcastTest || !broadcastPreview || broadcastPreview.recipients === 0}
className="px-5 py-2 rounded-full bg-[#e11d48] disabled:cursor-not-allowed disabled:opacity-40"
>

{sendingBroadcast
? (broadcastScheduleMode === "scheduled" ? "Scheduling..." : "Queuing...")
: (broadcastScheduleMode === "scheduled" ? "Schedule Campaign" : "Queue Campaign")}

</button>

</div>

<div className="mt-8 border-t border-white/10 pt-6">
  <div className="mb-3 flex items-center justify-between">
    <div>
      <h3 className="font-semibold text-white">Campaign History</h3>
      <p className="mt-1 text-xs text-zinc-500">The latest 100 campaign records are retained.</p>
    </div>
    <button type="button" onClick={loadEmailCampaigns} disabled={loadingEmailCampaigns} className="text-xs text-zinc-400 hover:text-white disabled:opacity-50">
      {loadingEmailCampaigns ? "Loading..." : "Refresh"}
    </button>
  </div>
  <div className="max-h-56 space-y-2 overflow-y-auto">
    {!loadingEmailCampaigns && emailCampaigns.length === 0 && (
      <p className="rounded-lg border border-dashed border-white/10 p-4 text-center text-sm text-zinc-500">No campaign history yet.</p>
    )}
    {emailCampaigns.map((campaign) => (
      <div key={campaign.id} className="rounded-lg border border-white/10 bg-[#050505] p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-white">{campaign.subject}</p>
            <p className="mt-1 text-xs text-zinc-500">
              {campaign.campaign_type} · {campaign.target} · {campaign.created_at ? new Date(campaign.created_at).toLocaleString() : "-"}
            </p>
            {campaign.sender_email && (
              <p className="mt-1 text-xs text-zinc-500">From: {campaign.sender_email}</p>
            )}
            {campaign.scheduled_at && (
              <p className="mt-1 text-xs text-sky-300">Scheduled: {new Date(campaign.scheduled_at).toLocaleString()}</p>
            )}
          </div>
          <span className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-semibold uppercase ${campaign.status === "completed" ? "bg-emerald-400/10 text-emerald-300" : campaign.status === "failed" ? "bg-red-400/10 text-red-300" : "bg-amber-400/10 text-amber-300"}`}>
            {campaign.status}
          </span>
        </div>
        <div className="mt-3 grid grid-cols-4 gap-2">
          <div className="rounded-md bg-emerald-400/5 p-2"><p className="text-[10px] text-zinc-500">Delivered</p><p className="font-semibold text-emerald-300">{campaign.delivered || 0}</p></div>
          <div className="rounded-md bg-red-400/5 p-2"><p className="text-[10px] text-zinc-500">Bounced</p><p className="font-semibold text-red-300">{campaign.bounced || 0}</p></div>
          <div className="rounded-md bg-sky-400/5 p-2"><p className="text-[10px] text-zinc-500">Opened</p><p className="font-semibold text-sky-300">{campaign.opened || 0}</p></div>
          <div className="rounded-md bg-violet-400/5 p-2"><p className="text-[10px] text-zinc-500">Clicked</p><p className="font-semibold text-violet-300">{campaign.clicked || 0}</p></div>
        </div>
        <p className="mt-2 text-xs text-zinc-500">
          {campaign.success || 0} accepted by provider · {campaign.failed || 0} send failed · {campaign.pending || 0} pending
          {campaign.retrying ? ` (${campaign.retrying} retrying)` : ""} · {(campaign.suppressed || 0) + (campaign.consent_excluded || 0) + (campaign.suppression_excluded || 0) + (campaign.product_excluded || 0)} excluded/suppressed
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => openCampaignDetails(campaign)}
            className="text-xs font-semibold text-sky-300 hover:underline"
          >
            View delivery details
          </button>
        {campaign.status === "scheduled" && (
          <button
            type="button"
            onClick={() => cancelScheduledCampaign(campaign)}
            className="text-xs font-semibold text-red-300 hover:underline"
          >
            Cancel schedule
          </button>
        )}
        </div>
      </div>
    ))}
  </div>
</div>

</div>

</div>

)}

      {campaignDetails && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm" onClick={() => setCampaignDetails(null)}>
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-2xl border border-white/10 bg-[#0a0a0c] p-6" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="truncate text-xl font-bold text-white">{campaignDetails.campaign.subject}</h3>
                <p className="mt-1 text-xs text-zinc-500">
                  {campaignDetails.campaign.campaign_type} · {campaignDetails.campaign.sender_email || "Sender not recorded"} · {campaignDetails.total} matching recipients
                </p>
              </div>
              <button type="button" onClick={() => setCampaignDetails(null)} className="rounded-lg p-2 text-zinc-400 hover:bg-white/5 hover:text-white"><X className="h-4 w-4" /></button>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded-lg border border-emerald-400/10 bg-emerald-400/5 p-3"><p className="text-xs text-zinc-500">Delivered</p><p className="mt-1 text-2xl font-bold text-emerald-300">{campaignDetails.campaign.delivered || 0}</p></div>
              <div className="rounded-lg border border-red-400/10 bg-red-400/5 p-3"><p className="text-xs text-zinc-500">Bounced</p><p className="mt-1 text-2xl font-bold text-red-300">{campaignDetails.campaign.bounced || 0}</p></div>
              <div className="rounded-lg border border-sky-400/10 bg-sky-400/5 p-3"><p className="text-xs text-zinc-500">Opened</p><p className="mt-1 text-2xl font-bold text-sky-300">{campaignDetails.campaign.opened || 0}</p></div>
              <div className="rounded-lg border border-violet-400/10 bg-violet-400/5 p-3"><p className="text-xs text-zinc-500">Clicked</p><p className="mt-1 text-2xl font-bold text-violet-300">{campaignDetails.campaign.clicked || 0}</p></div>
            </div>

            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                <input
                  value={campaignDeliverySearch}
                  onChange={(event) => setCampaignDeliverySearch(event.target.value)}
                  onKeyDown={(event) => { if (event.key === "Enter") loadCampaignDetails(campaignDetails.campaign.id); }}
                  placeholder="Search recipient name or email"
                  className="w-full rounded-lg border border-white/10 bg-[#050505] py-2.5 pl-10 pr-3 text-sm"
                />
              </div>
              <select
                value={campaignDeliveryStatus}
                onChange={(event) => {
                  setCampaignDeliveryStatus(event.target.value);
                  loadCampaignDetails(campaignDetails.campaign.id, campaignDeliverySearch, event.target.value);
                }}
                className="rounded-lg border border-white/10 bg-[#050505] px-3 py-2.5 text-sm"
              >
                <option value="all">All statuses</option>
                <option value="delivered">Delivered</option>
                <option value="bounced">Bounced</option>
                <option value="opened">Opened</option>
                <option value="clicked">Clicked</option>
                <option value="failed">Send failed</option>
                <option value="retrying">Retrying</option>
                <option value="pending">Pending</option>
                <option value="suppressed">Suppressed</option>
              </select>
              <button type="button" onClick={() => loadCampaignDetails(campaignDetails.campaign.id)} className="rounded-lg border border-white/10 px-4 py-2.5 text-sm font-semibold">
                {loadingCampaignDetails ? "Loading..." : "Search"}
              </button>
              {(campaignDetails.campaign.failed || 0) > 0 && (
                <button type="button" onClick={() => retryFailedCampaignDeliveries()} disabled={!!retryingCampaignDelivery} className="rounded-lg border border-amber-400/20 px-4 py-2.5 text-sm font-semibold text-amber-300 disabled:opacity-50">
                  {retryingCampaignDelivery === "all" ? "Queuing..." : "Retry all failed"}
                </button>
              )}
            </div>

            <div className="mt-4 overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full min-w-[850px] text-left text-xs">
                <thead className="bg-white/[0.03] text-zinc-500">
                  <tr><th className="px-3 py-3">Recipient</th><th className="px-3 py-3">Queue</th><th className="px-3 py-3">Provider</th><th className="px-3 py-3">Activity</th><th className="px-3 py-3">Attempts</th><th className="px-3 py-3">Failure reason</th><th className="px-3 py-3"></th></tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {campaignDetails.deliveries.map((delivery) => (
                    <tr key={delivery.id} className="text-zinc-300">
                      <td className="px-3 py-3"><p className="font-medium text-white">{delivery.recipient_name || "—"}</p><p className="mt-0.5 text-zinc-500">{delivery.recipient}</p></td>
                      <td className="px-3 py-3">{delivery.status}</td>
                      <td className="px-3 py-3">{delivery.provider_status || "—"}</td>
                      <td className="px-3 py-3 text-zinc-500">
                        {delivery.delivered_at ? "Delivered" : "—"}
                        {delivery.open_count ? ` · Open ${delivery.open_count}×` : ""}
                        {delivery.click_count ? ` · Click ${delivery.click_count}×` : ""}
                      </td>
                      <td className="px-3 py-3">{delivery.attempts || 0}</td>
                      <td className="max-w-[240px] px-3 py-3 text-red-300">{delivery.provider_error || delivery.last_error || "—"}</td>
                      <td className="px-3 py-3">
                        {(delivery.status === "failed" || delivery.provider_status === "failed") && (
                          <button type="button" onClick={() => retryFailedCampaignDeliveries(delivery.id)} disabled={!!retryingCampaignDelivery} className="font-semibold text-amber-300 hover:underline disabled:opacity-50">
                            {retryingCampaignDelivery === delivery.id ? "Queuing..." : "Retry"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!loadingCampaignDetails && campaignDetails.deliveries.length === 0 && (
                <p className="p-8 text-center text-sm text-zinc-500">No deliveries match this filter.</p>
              )}
            </div>
            {campaignDetails.total > campaignDetails.deliveries.length && (
              <p className="mt-3 text-xs text-zinc-500">Showing the first {campaignDetails.deliveries.length} of {campaignDetails.total} matching deliveries.</p>
            )}
          </div>
        </div>
      )}
      
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4" onClick={closeModal}>
          <form
            onSubmit={submitForm}
            onClick={(e) => e.stopPropagation()}
            data-testid="item-form-modal"
            className="w-full max-w-xl max-h-[90vh] overflow-y-auto bg-[#0a0a0c] border border-white/10 rounded-2xl p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-[Outfit] text-2xl font-bold">{modal.mode === "create" ? "Add" : "Edit"} {tabConfig.label.slice(0, -1)}</h3>
              <button type="button" onClick={closeModal} className="p-2 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-4">
              {SCHEMAS[tab].filter((f) => !f.showWhen || f.showWhen(form)).map((f) => (
                <div key={f.key}>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1.5">{f.label}{f.required && <span className="text-[#e11d48]">*</span>}</label>
                  {f.help && <p className="mb-2 text-xs leading-relaxed text-zinc-500">{f.help}</p>}
                  {f.type === "textarea" || f.type === "lines" ? (
                    <textarea
                      data-testid={`field-${f.key}`}
                      value={form[f.key] || ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      rows={3}
                      className="w-full bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#e11d48]"
                      placeholder={f.placeholder}
                      required={f.required}
                    />
                  ) : f.type === "textarea_lg" ? (
                    <textarea
                      data-testid={`field-${f.key}`}
                      value={form[f.key] || ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      rows={16}
                      className="w-full bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#e11d48]"
                      placeholder={f.placeholder || "# Heading\n\nWrite in **Markdown**..."}
                      required={f.required}
                    />
                  ) : f.type === "select" ? (
                    <select
                      data-testid={`field-${f.key}`}
                      value={form[f.key] || ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      required={f.required}
                      className="w-full bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#e11d48]"
                    >
                      <option value="">Choose...</option>
                      {(f.options || []).map((opt) => (
                        <option key={opt} value={opt}>{f.optionLabels?.[opt] || opt}</option>
                      ))}
                    </select>
                  ) : f.type === "product_select" ? (
                    <select
                      data-testid={`field-${f.key}`}
                      value={form[f.key] || ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      required={f.required}
                      className="w-full bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#e11d48]"
                    >
                      <option value="">Pilih product...</option>
                      {products
                        .filter((product) => !f.licensedOnly || product.requires_license)
                        .filter((product) =>
                          !f.excludeFree
                          || (!product.is_free && Number(product.price || 0) > 0)
                        )
                        .map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.name}
                          </option>
                        ))}
                    </select>
                  ) : f.type === "product_multi_select" ? (
                    <div className="space-y-2 rounded-lg border border-white/10 bg-[#050505] p-3">
                      {products
                        .filter((product) => product.id !== modal?.item?.id)
                        .map((product) => {
                          const selected = (form[f.key] || []).includes(product.id);
                          return (
                            <label key={product.id} className="flex items-center gap-2 text-sm text-zinc-300">
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={(event) => {
                                  const current = form[f.key] || [];
                                  setForm({
                                    ...form,
                                    [f.key]: event.target.checked
                                      ? [...current, product.id]
                                      : current.filter((id) => id !== product.id),
                                  });
                                }}
                                className="h-4 w-4 accent-[#e11d48]"
                              />
                              {product.name}
                            </label>
                          );
                        })}
                      {products.filter((product) => product.id !== modal?.item?.id).length === 0 && (
                        <p className="text-xs text-zinc-500">Belum ada produk lain yang dapat dipilih.</p>
                      )}
                    </div>
                  ) : f.type === "checkbox" ? (
                    <label className="flex items-center gap-2 text-sm text-zinc-300">
                      <input
                        data-testid={`field-${f.key}`}
                        type="checkbox"
                        checked={!!form[f.key]}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })}
                        className="w-4 h-4 accent-[#e11d48]"
                      />
                      Enable
                    </label>
                  ) : f.type === "file_or_url" ? (
                    <div className="space-y-2">
                      <input
                        data-testid={`field-${f.key}`}
                        type="text"
                        value={form[f.key] ?? ""}
                        onChange={(e) => setForm({
                          ...form,
                          [f.key]: e.target.value,
                          ...(f.storageKey ? {
                            [f.storageKey]: "",
                            [f.filenameKey]: "",
                          } : {}),
                        })}
                        placeholder={
                          f.storageKey && form[f.storageKey]
                            ? `File privat: ${form[f.filenameKey] || "tersimpan di R2"}`
                            : "https://... atau upload di bawah"
                        }
                        className="w-full bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#e11d48]"
                        required={f.required && !(f.storageKey && form[f.storageKey])}
                      />
                      <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-white/15 text-xs text-zinc-400 cursor-pointer hover:bg-white/5">
                        {uploadingProductField === f.key
                          ? <Loader2 className="w-4 h-4 text-[#e11d48] animate-spin" />
                          : <Upload className="w-4 h-4 text-[#e11d48]" />}
                        <span>
                          {uploadingProductField === f.key
                            ? `Mengunggah ${productUploadProgress}%`
                            : `${f.uploadLabel || "Upload product file"} (maks. ${f.storageKey ? "5 GB" : "500 MB"})`}
                        </span>
                        <input
                          data-testid={`upload-${f.key}`}
                          type="file"
                          className="hidden"
                          disabled={!!uploadingProductField}
                          onChange={(e) => uploadProductFile(e.target.files?.[0], f)}
                        />
                      </label>
                      {f.storageKey && form[f.storageKey] && (
                        <div className="text-[10px] text-emerald-400 font-mono truncate">
                          ✓ Privat R2: {form[f.filenameKey] || form[f.storageKey]}
                        </div>
                      )}
                      {form[f.key] && form[f.key].startsWith("http") && (
                        <a href={form[f.key]} target="_blank" rel="noreferrer" className="block text-[10px] text-emerald-400 font-mono truncate">
                          ✓ {form[f.key]}
                        </a>
                      )}
                    </div>
                  ) : (
                    <input
                      data-testid={`field-${f.key}`}
                      type={isIdrField(tab, f, form) ? "text" : (f.type || "text")}
                      inputMode={isIdrField(tab, f, form) ? "numeric" : undefined}
                      pattern={isIdrField(tab, f, form) ? "[0-9.]*" : undefined}
                      step={isIdrField(tab, f, form) ? undefined : f.step}
                      min={isIdrField(tab, f, form) ? undefined : f.min}
                      max={isIdrField(tab, f, form) ? undefined : (f.maxWhenPercent && form.upgrade_discount_type === "percent" ? f.maxWhenPercent : f.max)}
                      value={isIdrField(tab, f, form) ? formatIdrInput(form[f.key]) : (form[f.key] ?? "")}
                      onChange={(e) => setForm({ ...form, [f.key]: isIdrField(tab, f, form) ? normalizeIdrInput(e.target.value) : e.target.value })}
                      className="w-full bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#e11d48]"
                      placeholder={f.placeholder}
                      required={f.required}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-8 flex gap-3 justify-end">
              <button type="button" onClick={closeModal} className="px-5 py-2.5 rounded-full border border-white/15 text-sm font-semibold hover:bg-white/5">Cancel</button>
              <button data-testid="form-submit-btn" type="submit" disabled={saving} className="px-5 py-2.5 rounded-full bg-[#e11d48] hover:bg-[#be123c] text-sm font-semibold disabled:opacity-60">
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
